import React, { useEffect, useMemo, useState } from 'react';

interface PhysicsConceptSimulationProps {
  lessonSlug: string;
  pageIndex: number;
  pageData?: any;
  size?: number;
}

type SceneKind =
  | 'force'
  | 'gravity'
  | 'friction'
  | 'motion'
  | 'machine'
  | 'energy'
  | 'thermal'
  | 'wave'
  | 'momentum'
  | 'work'
  | 'rotation'
  | 'oscillator'
  | 'thermo'
  | 'gas'
  | 'fluid'
  | 'field'
  | 'phase'
  | 'continuum'
  | 'chaos';

type SimMode = 'calibration' | 'loop' | 'buttons' | 'slider';

type SimulationSpec = {
  id: string;
  hash: string;
  title: string;
  text: string;
  gradeLevel: number;
  sceneKind: SceneKind;
  mode: SimMode;
  buttons: string[];
  sliderLabel: string;
  labels: string[];
};

const shellStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 720,
  border: '1px solid rgba(148, 163, 184, 0.24)',
  borderRadius: 12,
  background: '#07111f',
  color: '#e5eefc',
  padding: 12,
  boxShadow: '0 18px 52px rgba(0, 0, 0, 0.26)',
};

const buttonStyle = (active = false): React.CSSProperties => ({
  border: active ? '1px solid rgba(125, 211, 252, 0.9)' : '1px solid rgba(148, 163, 184, 0.34)',
  background: active ? 'rgba(14, 165, 233, 0.24)' : 'rgba(15, 23, 42, 0.9)',
  color: '#e0f2fe',
  borderRadius: 8,
  padding: '8px 12px',
  fontWeight: 800,
  minWidth: 92,
});

const hashText = (value: string) => {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const getGradeLevel = (lessonSlug: string) => {
  if (lessonSlug.includes('uni')) return 13;
  const match = lessonSlug.match(/g(\d+)/i);
  return match ? Number(match[1]) : 10;
};

const titleCase = (value: string) =>
  value
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const compact = (value: string, fallback: string) => {
  const cleaned = value.replace(/\s+/g, ' ').replace(/[.]+$/, '').trim();
  if (!cleaned) return fallback;
  return cleaned.length > 28 ? `${cleaned.slice(0, 25)}...` : cleaned;
};

const getSceneKind = (text: string): SceneKind => {
  if (/\b(chaos|lyapunov|three-body|double pendulum|attractor|forecast|poincare)\b/i.test(text)) return 'chaos';
  if (/\b(lagrangian|hamiltonian|phase space|poisson|noether|coordinate|constraint|action)\b/i.test(text)) return 'phase';
  if (/\b(stress|strain|continuum|modulus|elastic solid|shear|bulk)\b/i.test(text)) return 'continuum';
  if (/\b(fluid|hydraulic|buoyancy|archimedes|pipe|wing|airfoil|viscosity|honey|laminar|turbulent|barometer)\b/i.test(text)) return 'fluid';
  if (/\b(gas|pressure|volume|pv|p-v|tire|molecule|kelvin|mean free path)\b/i.test(text)) return 'gas';
  if (/\b(carnot|entropy|thermodynamic|heat engine|absolute zero|zeroth|first law|second law|third law)\b/i.test(text)) return 'thermo';
  if (/\b(rotation|torque|angular|axis|wrench|seesaw|balance point|center of mass)\b/i.test(text)) return 'rotation';
  if (/\b(field|charge|magnetic|electric|flux|vector field|gravitational field)\b/i.test(text)) return 'field';
  if (/\b(wave|sound|frequency|amplitude|resonance|doppler|standing wave|seismic|slinky|light|mirror|prism|refraction)\b/i.test(text)) return 'wave';
  if (/\b(oscillat|spring|pendulum|hooke|restoring|period|damping)\b/i.test(text)) return 'oscillator';
  if (/\b(momentum|collision|crash|airbag|impulse|rocket|explosion|billiard)\b/i.test(text)) return 'momentum';
  if (/\b(work|power|joule|w\s*=\s*fd|ramp|dragging|rolling)\b/i.test(text)) return 'work';
  if (/\b(energy|kinetic|potential|battery|coaster|conservation|friction tax)\b/i.test(text)) return 'energy';
  if (/\b(heat|temperature|thermal|conduction|convection|radiation|thermos|metal|wood)\b/i.test(text)) return 'thermal';
  if (/\b(lever|fulcrum|wheel|axle|pulley|machine|inclined plane|screw|wedge)\b/i.test(text)) return 'machine';
  if (/\b(gravity|weight|fall|apple|moon|earth|orbit|inverse-square|escape velocity)\b/i.test(text)) return 'gravity';
  if (/\b(friction|brake|rough|smooth|grass|sliding|rolling)\b/i.test(text)) return 'friction';
  if (/\b(speed|velocity|acceleration|direction|relative|turning|motion)\b/i.test(text)) return 'motion';
  return 'force';
};

const extractButtons = (title: string, text: string, gradeLevel: number, sceneKind: SceneKind) => {
  const lower = `${title} ${text}`.toLowerCase();
  if (/balanced vs\.? unbalanced|unbalanced force|equilibrium/.test(lower)) return ['Even', 'Uneven'];
  if (/smooth vs\.? rough/.test(lower)) return ['Smooth', 'Rough'];
  if (/weight vs\.? mass|earth|moon/.test(lower) && gradeLevel <= 8) return ['Earth', 'Moon'];
  if (/push|pull/.test(lower) && gradeLevel <= 8) return ['Push', 'Pull'];
  if (/before|after/.test(lower)) return ['Before', 'After'];
  if (/elastic|inelastic/.test(lower)) return ['Elastic', 'Inelastic'];
  if (/laminar|turbulent/.test(lower)) return ['Laminar', 'Turbulent'];
  if (/earth|moon/.test(lower)) return ['Earth', 'Moon'];
  if (/hot|cold/.test(lower)) return ['Cold', 'Hot'];
  if (/transverse|longitudinal/.test(lower)) return ['Transverse', 'Longitudinal'];
  if (/p-wave|s-wave/.test(lower)) return ['P-wave', 'S-wave'];
  if (/open|closed|system/.test(lower) && sceneKind === 'momentum') return ['Open', 'Closed'];
  if (/button|toggle|switch/.test(lower)) return ['State A', 'State B'];
  return [];
};

const shouldUseSlider = (title: string, text: string, gradeLevel: number, sceneKind: SceneKind) => {
  if (gradeLevel <= 8) return ['The Feeling of Acceleration', 'The Distance Trade-Off'].includes(title);
  if (gradeLevel <= 9) return /\bslider\b/i.test(text) || ['energy', 'work', 'thermal', 'motion'].includes(sceneKind);
  return ['gas', 'thermo', 'phase', 'chaos', 'continuum', 'field'].includes(sceneKind) && !/\b(vs|versus|compare|toggle)\b/i.test(text);
};

const createSpec = (lessonSlug: string, pageIndex: number, pageData: any): SimulationSpec => {
  const title = pageData?.title ?? 'Physics Simulation';
  const description = pageData?.simulation?.description ?? '';
  const body = pageData?.content ?? '';
  const text = `${title} ${description} ${body}`;
  const hash = hashText(description || `${title}-${pageIndex}`);
  const gradeLevel = getGradeLevel(lessonSlug);
  let sceneKind = getSceneKind(text);
  if (['Pushes and Pulls', 'Balanced vs. Unbalanced'].includes(title)) sceneKind = 'force';
  if (['The Invisible Brake Pedal', 'Smooth vs. Rough', 'Braking vs. Pedaling'].includes(title)) sceneKind = 'friction';
  if (['The Rule of Laziness', 'Miles Per Hour', 'More Than Just Fast', 'Why Does Direction Matter?', 'Relative Speed', 'The Feeling of Acceleration', 'Turning is Accelerating'].includes(title)) sceneKind = 'motion';
  if (['The Head-On Collision'].includes(title)) sceneKind = 'momentum';
  if (['The Magic Beam', 'Trading Distance for Strength', 'The Wheel and Axle', 'The Inclined Plane', 'The Distance Trade-Off', 'A Wheel With A Rope'].includes(title)) sceneKind = 'machine';
  if (title === 'Defeating the Ground') sceneKind = 'gravity';
  if (['Energy in Action and in Waiting', "Gravity's Battery", 'The Great Transformation', 'The Rule That Cannot Be Broken', 'The Giant Machine'].includes(title)) sceneKind = 'energy';
  if (['The Microscopic Dance', 'One-Way Traffic', 'The Three Paths of Heat', 'The Floating Fluid', 'The Temperature Trick', 'The Thermos Magic'].includes(title)) sceneKind = 'thermal';
  if (['The Invisible Dominoes', 'The Anatomy of a Wave', 'The Straight and Narrow', 'The Speed of Light Changes', "Nature's Prism"].includes(title)) sceneKind = 'wave';
  if (['The Stubbornness of Stuff', 'The Seatbelt Savior', 'The Concept of Equilibrium', 'The Unbalanced Force', 'Mass and Inertia', 'Stopping Mass', 'The Classic Magic Trick', 'The Seatbelt Lock'].includes(title)) sceneKind = 'motion';
  if (['The Master Equation', 'The Math of the Push', 'The Mass Penalty', 'The Math of the Heavy', 'The Universal Constant', 'Weight is a Force', 'Adding Up The Pushes', 'The Math of the Tug'].includes(title)) sceneKind = 'force';
  if (['The Law of Action and Reaction', 'The Rocket in the Vacuum', 'Mass in Motion', 'The Formula for Destruction', 'The Problem of Stopping', 'Stretching the Clock', 'The Law of Conservation of Momentum', 'Inelastic Collisions'].includes(title)) sceneKind = 'momentum';
  if (gradeLevel === 10 && [
    'Mass in Motion',
    'The Equation: p = mv',
    'Direction Matters',
    'The Physics of Stopping',
    'Engineering Survival',
    'Rolling With The Punches',
    'The Accounting Rule of the Universe',
    'Adding the Vectors',
    'The Collision Math',
    'Solving for the Final Speed',
    'The Energy Difference',
    'The Perfect Bounce',
    'The Perfectly Inelastic Crash',
    'The Ballistic Pendulum',
    'Zero Initial Momentum',
    'Rocket Propulsion Revisited',
    'The Deadly Bounce',
    "The Seatbelt's Job",
  ].includes(title)) sceneKind = 'momentum';
  if (gradeLevel === 10 && [
    'The Single Balance Point',
    'Multi-Object Systems',
    'Symmetry vs Asymmetry',
    'The Plumb Line Test',
    'Torque: The Rotational Push',
    'Rotational Equilibrium',
    'The Base of Support',
    'High vs Low Centers',
  ].includes(title)) sceneKind = 'rotation';
  const buttons = extractButtons(title, text, gradeLevel, sceneKind).slice(0, 2);
  const hasCalibration = gradeLevel === 9 || gradeLevel === 10 || [
    'Pushes and Pulls',
    'Balanced vs. Unbalanced',
    'The Invisible Pull',
    'Weight vs. Mass',
    'The Invisible Brake Pedal',
    'Smooth vs. Rough',
    'The Rule of Laziness',
    'Braking vs. Pedaling',
    'Miles Per Hour',
    'More Than Just Fast',
    'Why Does Direction Matter?',
    'Relative Speed',
    'The Head-On Collision',
    'The Feeling of Acceleration',
    'Turning is Accelerating',
    'The Magic Beam',
    'Trading Distance for Strength',
    'Defeating the Ground',
    'The Wheel and Axle',
    'The Inclined Plane',
    'The Distance Trade-Off',
    'A Wheel With A Rope',
    'Multiplying Strength',
    'Energy in Action and in Waiting',
    "Gravity's Battery",
    'The Great Transformation',
    'The Rule That Cannot Be Broken',
    'The Giant Machine',
    'The Microscopic Dance',
    'One-Way Traffic',
    'The Three Paths of Heat',
    'The Floating Fluid',
    'The Temperature Trick',
    'The Thermos Magic',
    'The Invisible Dominoes',
    'The Anatomy of a Wave',
    'The Straight and Narrow',
    'The Speed of Light Changes',
    "Nature's Prism",
    'The Stubbornness of Stuff',
    'The Seatbelt Savior',
    'The Concept of Equilibrium',
    'The Unbalanced Force',
    'Mass and Inertia',
    'Stopping Mass',
    'The Classic Magic Trick',
    'The Seatbelt Lock',
    'The Master Equation',
    'The Math of the Push',
    'The Mass Penalty',
    'The Math of the Heavy',
    'The Universal Constant',
    'Weight is a Force',
    'Adding Up The Pushes',
    'The Math of the Tug',
    'The Law of Action and Reaction',
    'The Rocket in the Vacuum',
    'Mass in Motion',
    'The Formula for Destruction',
    'The Problem of Stopping',
    'Stretching the Clock',
    'The Law of Conservation of Momentum',
    'Inelastic Collisions',
  ].includes(title);
  const mode: SimMode = hasCalibration
    ? 'calibration'
    : buttons.length
      ? 'buttons'
      : shouldUseSlider(title, text, gradeLevel, sceneKind)
        ? 'slider'
        : 'loop';

  return {
    id: `${lessonSlug}-p${pageIndex}-${hash}`,
    hash,
    title,
    text,
    gradeLevel,
    sceneKind,
    mode,
    buttons,
    sliderLabel: sceneKind === 'gas'
      ? 'Temperature'
      : sceneKind === 'field'
        ? 'Distance'
        : sceneKind === 'chaos'
          ? 'Initial difference'
          : sceneKind === 'motion'
            ? 'Speed'
            : titleCase(sceneKind),
    labels: getSceneLabels(title, text, sceneKind),
  };
};

const getSceneLabels = (title: string, text: string, sceneKind: SceneKind) => {
  const labels: string[] = [];
  const lower = `${title} ${text}`.toLowerCase();
  if (/apple/.test(lower)) labels.push('apple');
  if (/ball/.test(lower)) labels.push('ball');
  if (/rock/.test(lower)) labels.push('rock');
  if (/person|student|passenger|dummy/.test(lower)) labels.push('person');
  if (/cart|car|truck|bicycle|rocket|coaster/.test(lower)) labels.push('vehicle');
  if (/box|block|door|wall/.test(lower)) labels.push('box');
  if (/spring|pendulum/.test(lower)) labels.push('oscillator');
  if (/airbag|collision|crash/.test(lower)) labels.push('crash test');
  if (/water|honey|fluid|pipe|ship|wing/.test(lower)) labels.push('fluid');
  if (/gas|pressure|molecule|tire/.test(lower)) labels.push('gas');
  if (!labels.length) labels.push(titleCase(sceneKind));
  return labels.slice(0, 3);
};

const Arrow = ({
  x1,
  y1,
  x2,
  y2,
  color,
  label,
  width = 5,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  label?: string;
  width?: number;
}) => {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const head = 14 + width;
  const a1 = angle + Math.PI * 0.82;
  const a2 = angle - Math.PI * 0.82;
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={width} strokeLinecap="round" />
      <polygon
        points={`${x2},${y2} ${x2 + Math.cos(a1) * head},${y2 + Math.sin(a1) * head} ${x2 + Math.cos(a2) * head},${y2 + Math.sin(a2) * head}`}
        fill={color}
      />
      {label && (
        <text x={(x1 + x2) / 2} y={(y1 + y2) / 2 - 10} fill={color} fontSize="14" fontWeight="900" textAnchor="middle">
          {label}
        </text>
      )}
    </g>
  );
};

const Ground = ({ y = 300, rough = false }: { y?: number; rough?: boolean }) => (
  <g>
    <rect x="62" y={y} width="596" height="18" rx="9" fill={rough ? '#5b4a32' : '#64748b'} />
    {rough &&
      Array.from({ length: 28 }).map((_, index) => (
        <path key={index} d={`M${72 + index * 20} ${y + 2} l8 -10 l8 10`} fill="none" stroke="#9ca36a" strokeWidth="2" opacity="0.8" />
      ))}
  </g>
);

const Ball = ({ x, y, color = '#60a5fa' }: { x: number; y: number; color?: string }) => (
  <g>
    <circle cx={x} cy={y} r="28" fill={color} />
    <path d={`M${x - 16} ${y - 8} C${x - 2} ${y - 20}, ${x + 8} ${y + 18}, ${x + 20} ${y + 4}`} fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="4" />
  </g>
);

const Box = ({ x, y, color, label }: { x: number; y: number; color: string; label?: string }) => (
  <g>
    <rect x={x} y={y} width="86" height="72" rx="8" fill={color} />
    {label && <text x={x + 43} y={y + 42} fill="#082f49" fontSize="16" fontWeight="900" textAnchor="middle">{label}</text>}
  </g>
);

const Person = ({ x, y }: { x: number; y: number }) => (
  <g stroke="#e0f2fe" strokeWidth="8" strokeLinecap="round" fill="none">
    <circle cx={x} cy={y} r="22" fill="#93c5fd" stroke="none" />
    <line x1={x} y1={y + 24} x2={x} y2={y + 92} />
    <line x1={x - 38} y1={y + 52} x2={x + 38} y2={y + 52} />
    <line x1={x} y1={y + 92} x2={x - 28} y2={y + 136} />
    <line x1={x} y1={y + 92} x2={x + 28} y2={y + 136} />
  </g>
);

const Car = ({ x, y, scale = 1, color = '#38bdf8' }: { x: number; y: number; scale?: number; color?: string }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`}>
    <rect x="-54" y="-24" width="108" height="42" rx="12" fill={color} />
    <rect x="-26" y="-48" width="58" height="30" rx="10" fill="rgba(224,242,254,0.65)" />
    <circle cx="-34" cy="24" r="13" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
    <circle cx="34" cy="24" r="13" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
  </g>
);

const Bicycle = ({ x, y, braking = false, pedaling = false, time = 0 }: { x: number; y: number; braking?: boolean; pedaling?: boolean; time?: number }) => {
  const pedal = pedaling ? Math.sin(time / 3) * 18 : 0;
  return (
    <g transform={`translate(${x} ${y})`}>
      <circle cx="-42" cy="40" r="34" fill="none" stroke="#e0f2fe" strokeWidth="7" />
      <circle cx="54" cy="40" r="34" fill="none" stroke="#e0f2fe" strokeWidth="7" />
      <path d="M-42 40 L0 4 L54 40 L-6 40 L-42 40 M0 4 L22 -28" fill="none" stroke="#38bdf8" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="22" y1="-28" x2="60" y2="-38" stroke="#e0f2fe" strokeWidth="6" strokeLinecap="round" />
      <circle cx="-6" cy="40" r="8" fill="#facc15" />
      <line x1="-6" y1="40" x2={-6 + Math.cos(pedal) * 28} y2={40 + Math.sin(pedal) * 28} stroke="#facc15" strokeWidth="5" strokeLinecap="round" />
      {braking && (
        <>
          <rect x="33" y="2" width="18" height="30" rx="5" fill="#ef4444" transform="rotate(-18 42 17)" />
          <rect x="59" y="2" width="18" height="30" rx="5" fill="#ef4444" transform="rotate(18 68 17)" />
          <text x="54" y="-8" fill="#fecaca" fontSize="12" fontWeight="900" textAnchor="middle">brake pads clamp</text>
        </>
      )}
    </g>
  );
};

const EnergyBar = ({ x, y, label, value, color }: { x: number; y: number; label: string; value: number; color: string }) => (
  <g>
    <text x={x} y={y - 8} fill="#e0f2fe" fontSize="13" fontWeight="900">{label}</text>
    <rect x={x} y={y} width="128" height="16" rx="8" fill="#172033" stroke="rgba(148,163,184,0.35)" />
    <rect x={x} y={y} width={Math.max(0, Math.min(1, value)) * 128} height="16" rx="8" fill={color} />
  </g>
);

const Runner = ({ x, y, color, label }: { x: number; y: number; color: string; label: string }) => (
  <g transform={`translate(${x} ${y})`} stroke={color} strokeWidth="6" strokeLinecap="round" fill="none">
    <circle cx="0" cy="-42" r="13" fill={color} stroke="none" />
    <line x1="0" y1="-28" x2="0" y2="8" />
    <line x1="0" y1="-8" x2="-22" y2="4" />
    <line x1="0" y1="-8" x2="22" y2="-20" />
    <line x1="0" y1="8" x2="-18" y2="34" />
    <line x1="0" y1="8" x2="22" y2="34" />
    <text x="0" y="54" fill={color} stroke="none" fontSize="14" fontWeight="900" textAnchor="middle">{label}</text>
  </g>
);

const Molecules = ({ hot = false, time = 0, count = 26, x = 152, y = 100, width = 300, height = 170 }: { hot?: boolean; time?: number; count?: number; x?: number; y?: number; width?: number; height?: number }) => (
  <g>
    {Array.from({ length: count }).map((_, index) => {
      const amplitude = hot ? 18 : 5;
      const speed = hot ? 1.8 : 0.45;
      const px = x + 20 + ((index * 43) % (width - 40)) + Math.sin(time / (4 / speed) + index) * amplitude;
      const py = y + 18 + ((index * 31) % (height - 36)) + Math.cos(time / (5 / speed) + index * 1.7) * amplitude;
      return <circle key={index} cx={px} cy={py} r="6" fill={hot ? '#fb7185' : '#93c5fd'} opacity="0.95" />;
    })}
  </g>
);

const Pie = ({ x, y, heat }: { x: number; y: number; heat: number }) => {
  const kinetic = Math.max(0.08, (1 - heat) * 0.46);
  const potential = Math.max(0.08, (1 - heat) * 0.46);
  const thermal = Math.max(0.1, heat);
  const total = kinetic + potential + thermal;
  const values = [
    { value: kinetic / total, color: '#ef4444', label: 'K' },
    { value: potential / total, color: '#38bdf8', label: 'P' },
    { value: thermal / total, color: '#94a3b8', label: 'Heat' },
  ];
  let start = -Math.PI / 2;
  return (
    <g>
      {values.map((slice) => {
        const end = start + slice.value * Math.PI * 2;
        const large = end - start > Math.PI ? 1 : 0;
        const x1 = x + Math.cos(start) * 62;
        const y1 = y + Math.sin(start) * 62;
        const x2 = x + Math.cos(end) * 62;
        const y2 = y + Math.sin(end) * 62;
        const d = `M${x},${y} L${x1},${y1} A62,62 0 ${large},1 ${x2},${y2} Z`;
        start = end;
        return <path key={slice.label} d={d} fill={slice.color} stroke="#08111f" strokeWidth="3" />;
      })}
      <text x={x} y={y + 5} fill="#020617" fontSize="15" fontWeight="900" textAnchor="middle">100%</text>
    </g>
  );
};

const Grade9Scene = ({ spec, selected, slider, sliderB = 45, actionCount, time }: { spec: SimulationSpec; selected: number; slider: number; sliderB?: number; actionCount: number; time: number }) => {
  const title = spec.title;
  const phase = (time % 100) / 100;
  const a = slider / 100;
  const b = sliderB / 100;
  const moving = selected > 0 || actionCount > 0;

  if (title === 'The Physics Definition of Work') {
    const boxX = 110 + phase * 180;
    return (
      <>
        <rect x="48" y="82" width="300" height="250" rx="16" fill="#082f49" />
        <rect x="372" y="82" width="300" height="250" rx="16" fill="#1f2937" />
        <Ground y={284} /><Person x={95 + phase * 80} y={138} /><Box x={boxX} y="212" color="#7dd3fc" label="box" />
        <Arrow x1={boxX - 80} y1="190" x2={boxX + 70} y2="190" color="#22c55e" label="force + distance" width={6} />
        <Person x={438} y={138} /><rect x="574" y="166" width="44" height="118" rx="5" fill="#b45309" />
        <Arrow x1="476" y1="206" x2="570" y2="206" color="#fb7185" label="force, no distance" width={6} />
        <text x="198" y="118" fill="#22c55e" fontSize="18" fontWeight="900" textAnchor="middle">Work Done: {Math.round(phase * 80)} J</text>
        <text x="522" y="118" fill="#facc15" fontSize="18" fontWeight="900" textAnchor="middle">Work Done: 0 J</text>
      </>
    );
  }

  if (title === 'The Formula: W = Fd') {
    const force = Math.round(5 + a * 45);
    const distance = Math.round(1 + b * 9);
    const boxX = 120 + distance * 42;
    return (
      <>
        <Ground y={314} />
        {Array.from({ length: 11 }).map((_, index) => <line key={index} x1={120 + index * 42} y1="268" x2={120 + index * 42} y2="326" stroke="#94a3b8" strokeWidth="2" />)}
        <Box x={boxX} y="242" color="#7dd3fc" label="block" />
        <Arrow x1="120" y1="210" x2={120 + force * 7} y2="210" color="#22c55e" label={`${force} N`} width={5 + a * 5} />
        <text x="360" y="112" fill="#facc15" fontSize="24" fontWeight="900" textAnchor="middle">{force} N × {distance} m = {force * distance} Joules</text>
      </>
    );
  }

  if (title === 'The Direction Trap') {
    const walk = selected === 1;
    const x = walk ? 260 + phase * 220 : 300;
    const boxY = walk ? 174 : 240 - Math.min(1, phase * 1.4) * 70;
    return (
      <>
        <Ground y={322} />
        <Person x={x} y={170} /><Box x={x - 18} y={boxY} color="#facc15" label="box" />
        <Arrow x1={x + 80} y1={walk ? 214 : 266} x2={x + 80} y2={walk ? 150 : 190} color="#38bdf8" label="force up" width={5} />
        <Arrow x1={x - 70} y1={walk ? 184 : 266} x2={walk ? x + 80 : x - 70} y2={walk ? 184 : 190} color="#22c55e" label={walk ? 'motion right' : 'motion up'} width={5} />
        <text x="360" y="108" fill={walk ? '#facc15' : '#22c55e'} fontSize="22" fontWeight="900" textAnchor="middle">{walk ? 'Work: 0 J' : 'Work: force and motion match'}</text>
      </>
    );
  }

  if (title === 'Angles and Work') {
    const angle = a * Math.PI / 2;
    const useful = Math.cos(angle);
    const sledX = 250 + phase * (60 + useful * 260);
    return (
      <>
        <Ground y={318} />
        <rect x={sledX} y="258" width="120" height="42" rx="16" fill="#38bdf8" />
        <line x1={sledX + 20} y1="258" x2={sledX - 150 * Math.cos(angle)} y2={258 - 150 * Math.sin(angle)} stroke="#e0f2fe" strokeWidth="7" strokeLinecap="round" />
        <EnergyBar x={430} y={118} label="Useful Forward Force" value={useful} color="#22c55e" />
        <text x="214" y="112" fill="#facc15" fontSize="20" fontWeight="900" textAnchor="middle">Rope angle: {Math.round(a * 90)}°</text>
      </>
    );
  }

  if (title === 'Working Against Friction') {
    const sand = selected === 1;
    const friction = sand ? 0.72 : 0.18;
    const moves = a > friction;
    const x = 150 + (moves ? phase * (140 + a * 250) : 0);
    return (
      <>
        <Ground y={314} rough={sand} />
        <Box x={x} y="242" color="#7dd3fc" label="box" />
        <Arrow x1={x - 78} y1="204" x2={x + 80 + a * 130} y2="204" color="#22c55e" label="push force" width={5 + a * 5} />
        <Arrow x1={x + 88} y1="330" x2={x + 88 - 70 - friction * 145} y2="330" color="#fb7185" label={sand ? 'sandpaper friction' : 'ice friction'} width={4 + friction * 6} />
      </>
    );
  }

  if (title === "The Wheel's Magic") {
    const x = 130 + phase * 390;
    return (
      <>
        <Ground y={182} rough /><Ground y={326} />
        <Box x={x} y="110" color="#a16207" label="drag" />
        {Array.from({ length: 6 }).map((_, i) => <Arrow key={i} x1={x + 18 + i * 12} y1="194" x2={x - 22 + i * 12} y2="194" color="#fb7185" width={4} />)}
        <rect x={x} y="246" width="100" height="42" rx="8" fill="#7dd3fc" />
        <circle cx={x + 20} cy="302" r="14" fill="#020617" stroke="#e0f2fe" strokeWidth="4" /><circle cx={x + 78} cy="302" r="14" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <Arrow x1={x + 20} y1="326" x2={x - 10} y2="326" color="#fb7185" width={2} /><Arrow x1={x + 78} y1="326" x2={x + 48} y2="326" color="#fb7185" label="tiny contact friction" width={2} />
      </>
    );
  }

  if (title === 'The Great Trade-Off' || title === 'Mechanical Advantage') {
    const length = 3 + a * 7;
    const force = 2000 / length;
    const ma = length / 2;
    const topY = 178;
    return (
      <>
        <rect x="520" y={topY} width="72" height={330 - topY} fill="#475569" />
        <polygon points={`120,330 520,330 520,${topY}`} fill="#64748b" />
        <Box x={170 + phase * 300} y={300 - phase * 120} color="#7dd3fc" label={title === 'The Great Trade-Off' ? 'piano' : 'load'} />
        <text x="360" y="86" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">{title === 'The Great Trade-Off' ? 'Total Work: 2000 J' : `Mechanical Advantage: ${ma.toFixed(1)}×`}</text>
        <text x="360" y="122" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{title === 'The Great Trade-Off' ? `Force ${Math.round(force)} N × Distance ${length.toFixed(1)} m` : `Force needed shrinks as ramp length grows`}</text>
        <EnergyBar x={460} y={246} label="Force needed" value={1 / ma} color="#22c55e" />
      </>
    );
  }

  if (title === 'The Kinetic Energy Equation') {
    const speed = Math.round(10 + a * 50);
    const ke = (speed * speed) / 3600;
    return (
      <>
        <Ground y={314} /><Car x={120 + phase * (140 + a * 380)} y={282} color="#38bdf8" />
        <rect x="454" y="104" width="42" height="220" fill="#172033" />
        <rect x="454" y={324 - Math.min(1, ke) * 220} width="42" height={Math.min(1, ke) * 220} fill="#f97316" />
        <text x="360" y="104" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">Speed: {speed} mph • KE rises with v²</text>
      </>
    );
  }

  if (title === 'Doing the Math' && /KE =|Kinetic Energy/i.test(spec.text)) {
    const mass = Math.round(2 + a * 18);
    const velocity = Math.round(1 + b * 9);
    const square = velocity * velocity;
    const energy = Math.round(square * mass / 2);
    const pinSpread = Math.min(1, energy / 500);
    return (
      <>
        <Ground y={314} /><Ball x={120 + phase * 330} y={282} color="#facc15" />
        {Array.from({ length: 5 }).map((_, i) => <rect key={i} x={500 + i * 22 + pinSpread * i * 18} y={238 + (i % 2) * 24 - pinSpread * 45} width="12" height="54" rx="6" fill="#e0f2fe" />)}
        <text x="360" y="94" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{velocity}² = {square} • {square} × {mass} = {square * mass} • ÷2 = {energy} J</text>
      </>
    );
  }

  if (title === 'The Equation for Height') {
    const mass = 10 + Math.round(a * 90);
    const height = 1 + Math.round(b * 9);
    const stored = mass * 10 * height;
    return (
      <>
        <rect x="130" y="86" width="18" height="238" fill="#64748b" /><line x1="139" y1="92" x2="360" y2="92" stroke="#64748b" strokeWidth="10" />
        <Box x="330" y={300 - height * 18} color="#94a3b8" label="beam" />
        <EnergyBar x={468} y={148} label="Stored Joules" value={stored / 10000} color="#38bdf8" />
        <text x="360" y="356" fill="#facc15" fontSize="18" fontWeight="900" textAnchor="middle">PE = {mass} × 10 × {height} = {stored} J</text>
      </>
    );
  }

  if (title === 'Doing the Math' && /PE =|Potential Energy/i.test(spec.text)) {
    return (
      <>
        <rect x="90" y="252" width="230" height="86" fill="#78350f" /><rect x="90" y="120" width="82" height="218" fill="#854d0e" />
        <circle cx="142" cy="104" r="28" fill="#78716c" filter={moving ? `url(#simple-glow-${spec.id})` : undefined} />
        <rect x="290" y="128" width="84" height="42" rx="8" fill="#38bdf8" /><text x="332" y="154" fill="#082f49" fontSize="15" fontWeight="900" textAnchor="middle">10kg</text>
        <rect x="392" y="128" width="84" height="42" rx="8" fill="#38bdf8" /><text x="434" y="154" fill="#082f49" fontSize="15" fontWeight="900" textAnchor="middle">9.8g</text>
        <rect x="494" y="128" width="84" height="42" rx="8" fill="#38bdf8" /><text x="536" y="154" fill="#082f49" fontSize="15" fontWeight="900" textAnchor="middle">5m</text>
        <text x="438" y="230" fill="#e0f2fe" fontSize="22" fontWeight="900" textAnchor="middle">PE = mass × gravity × height</text>
        <text x="438" y="276" fill="#facc15" fontSize="24" fontWeight="900" textAnchor="middle">500 J Stored</text>
      </>
    );
  }

  if (title === 'The Energy Trade') {
    const y = 96 + phase * 220;
    return (
      <>
        <Ground y={330} /><Ball x={260} y={y} color="#facc15" />
        <EnergyBar x={430} y={132} label="PE" value={1 - phase} color="#38bdf8" />
        <EnergyBar x={430} y={188} label="KE" value={phase} color="#ef4444" />
        <text x="360" y="362" fill="#e0f2fe" fontSize="16" fontWeight="900" textAnchor="middle">PE + KE = 100 J</text>
      </>
    );
  }

  if (title === 'Setting the Equations Equal') {
    const y = 126 + phase * 188;
    return (
      <>
        <text x="360" y="100" fill="#facc15" fontSize="30" fontWeight="900" textAnchor="middle">mgh = ½mv²</text>
        <line x1="254" y1="88" x2="280" y2="106" stroke="#ef4444" strokeWidth="5" /><line x1="440" y1="88" x2="466" y2="106" stroke="#ef4444" strokeWidth="5" />
        <Ground y={330} /><circle cx="270" cy={y} r="30" fill="#57534e" /><circle cx="450" cy={y} r="20" fill="#a16207" />
        <text x="360" y="362" fill="#22c55e" fontSize="18" fontWeight="900" textAnchor="middle">same height means same impact speed</text>
      </>
    );
  }

  if (title === 'Elastic Potential Energy') {
    const stretch = a;
    const released = selected === 1;
    const blockX = released ? 420 - phase * 220 : 420 - stretch * 190;
    return (
      <>
        <Ground y={314} /><path d={Array.from({ length: 26 }).map((_, i) => `${i === 0 ? 'M' : 'L'}${150 + i * ((blockX - 150) / 25)},${270 + Math.sin(i * Math.PI) * 24}`).join(' ')} fill="none" stroke="#e0f2fe" strokeWidth="6" />
        <Box x={blockX} y="238" color="#7dd3fc" label="block" />
        <EnergyBar x={456} y={126} label="Stored Elastic Energy" value={released ? 1 - phase : stretch} color="#38bdf8" />
        <EnergyBar x={456} y={178} label="Kinetic Energy" value={released ? Math.max(0, 1 - phase * 0.8) : 0} color="#ef4444" />
      </>
    );
  }

  if (title === "Hooke's Law (Concept)") {
    return (
      <>
        <path d="M120 160 C160 110, 200 210, 240 160 S320 160, 360 160" fill="none" stroke="#38bdf8" strokeWidth="6" />
        <path d="M120 278 C138 248, 156 308, 174 278 S210 278, 228 278" fill="none" stroke="#f97316" strokeWidth="12" />
        <Arrow x1="380" y1="160" x2="540" y2="160" color="#22c55e" label="10N stretches slinky far" width={5} />
        <Arrow x1="248" y1="278" x2="312" y2="278" color="#22c55e" label="10N barely moves stiff spring" width={5} />
      </>
    );
  }

  if (title === 'The Law of Conservation of Energy') {
    const heat = selected === 2 ? 0.65 : selected === 1 ? 0.2 : 0.35;
    return (
      <>
        <Pie x={360} y={190} heat={heat} />
        <text x="360" y="92" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">Total Universe Energy</text>
        <text x="360" y="300" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{selected === 1 ? 'Chemical → Kinetic rocket' : selected === 2 ? 'Kinetic → Thermal sliding box' : 'Potential → Kinetic falling ball'}</text>
      </>
    );
  }

  if (title === 'The Friction Tax' && /pendulum|hinge|bob/i.test(spec.text)) {
    const damp = 1 - phase * 0.5;
    const angle = Math.sin(time / 12) * damp;
    const bobX = 360 + Math.sin(angle) * 170;
    const bobY = 112 + Math.cos(angle) * 170;
    return (
      <>
        <line x1="360" y1="112" x2={bobX} y2={bobY} stroke="#e0f2fe" strokeWidth="5" /><circle cx={bobX} cy={bobY} r="30" fill="#facc15" />
        <path d="M330 112 C310 90, 310 134, 286 112" fill="none" stroke="#fb7185" strokeWidth="4" />
        <path d={`M${bobX - 36} ${bobY} C${bobX - 76} ${bobY - 24}, ${bobX - 76} ${bobY + 24}, ${bobX - 112} ${bobY}`} fill="none" stroke="#fb7185" strokeWidth="4" />
        <text x="360" y="358" fill="#22c55e" fontSize="18" fontWeight="900" textAnchor="middle">Kinetic + Potential + Heat = 100%</text>
      </>
    );
  }

  if (title === 'The Element of Time') {
    const sprint = selected === 1;
    const p = sprint ? Math.min(1, phase * 1.8) : phase;
    return (
      <>
        <path d="M120 320 H250 V270 H380 V220 H510 V170 H610" fill="none" stroke="#64748b" strokeWidth="12" />
        <Person x={132 + p * 420} y={210 - p * 112} />
        <Box x={130 + p * 420} y={172 - p * 112} color="#facc15" label="50kg" />
        <text x="360" y="94" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">Total Work: 5000 J</text>
        <EnergyBar x={460} y={122} label="Power" value={sprint ? 0.95 : 0.28} color={sprint ? '#ef4444' : '#22c55e'} />
      </>
    );
  }

  if (title === 'The Power Formula') {
    const seconds = 1 + Math.round(a * 99);
    const power = Math.round(10000 / seconds);
    return (
      <>
        <text x="360" y="112" fill="#facc15" fontSize="24" fontWeight="900" textAnchor="middle">Work locked: 10,000 J</text>
        <text x="360" y="166" fill="#e0f2fe" fontSize="28" fontWeight="900" textAnchor="middle">Power = 10000 / {seconds}s = {power} W</text>
        <EnergyBar x={276} y={226} label="Power bar" value={power / 10000} color="#38bdf8" />
      </>
    );
  }

  if (title === 'The Efficiency Rating') {
    const p = moving ? phase : 0;
    return (
      <>
        <rect x="284" y="146" width="152" height="108" rx="20" fill="#475569" />
        <polygon points="320,100 400,100 360,146" fill="#94a3b8" />
        <path d="M330 254 C250 302, 186 302, 130 330" fill="none" stroke="#22c55e" strokeWidth="14" />
        <path d="M390 254 C480 304, 550 304, 620 330" fill="none" stroke="#ef4444" strokeWidth="14" />
        {Array.from({ length: 12 }).map((_, i) => <circle key={i} cx={350 + Math.sin(i) * 36 + p * (i < 4 ? -190 : 190)} cy={130 + p * 190 + (i % 4) * 8} r="7" fill="#facc15" />)}
        <text x="360" y="84" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">Efficiency: 30%</text>
      </>
    );
  }

  if (title === 'The Friction Tax' && /engine|piston|Lubrication/i.test(spec.text)) {
    const friction = 1 - a;
    return (
      <>
        <rect x="250" y="96" width="220" height="228" rx="18" fill={friction > 0.55 ? '#7f1d1d' : '#334155'} stroke="#94a3b8" strokeWidth="7" />
        <rect x="292" y={142 + Math.sin(time / 5) * 72} width="136" height="54" rx="12" fill="#e0f2fe" />
        <line x1="360" y1={196 + Math.sin(time / 5) * 72} x2="360" y2="318" stroke="#e0f2fe" strokeWidth="12" />
        <EnergyBar x={72} y={132} label="Forward Power" value={a} color="#22c55e" />
        <EnergyBar x={72} y={190} label="Waste Heat" value={friction} color="#ef4444" />
      </>
    );
  }

  if (title === 'The Ultimate Energy Laboratory') {
    const p = phase;
    const y = 260 - Math.sin(p * Math.PI * 2) * 80;
    const potential = (300 - y) / 170;
    return (
      <>
        <path d="M70 290 C160 82, 270 332, 386 150 S560 288, 650 150" fill="none" stroke="#94a3b8" strokeWidth="10" />
        <rect x={90 + p * 520} y={y} width="62" height="30" rx="8" fill="#facc15" />
        <rect x="160" y="88" width="400" height="28" rx="14" fill="#ef4444" />
        <rect x="160" y="88" width={400 * Math.max(0, Math.min(1, potential))} height="28" rx="14" fill="#38bdf8" />
        <text x="360" y="72" fill="#e0f2fe" fontSize="16" fontWeight="900" textAnchor="middle">Total Mechanical Energy stays fixed</text>
      </>
    );
  }

  if (title === 'The Friction Drain') {
    const lost = phase * 0.6;
    return (
      <>
        <path d="M70 292 C172 88, 280 332, 392 184 S548 282, 640 202" fill="none" stroke="#94a3b8" strokeWidth="10" />
        <rect x={90 + phase * 500} y={250 - Math.sin(phase * Math.PI * 1.4) * 80} width="62" height="30" rx="8" fill="#facc15" />
        <Pie x={530} y={158} heat={lost} />
        <text x="530" y="250" fill="#e0f2fe" fontSize="15" fontWeight="900" textAnchor="middle">Lost to Friction grows</text>
      </>
    );
  }

  return null;
};

const Grade10Scene = ({ spec, selected, slider, sliderB = 45, actionCount, time }: { spec: SimulationSpec; selected: number; slider: number; sliderB?: number; actionCount: number; time: number }) => {
  const title = spec.title;
  const phase = (time % 100) / 100;
  const a = slider / 100;
  const b = sliderB / 100;
  const started = actionCount > 0;
  const Label = ({ x, y, children, color = '#e0f2fe', size = 15 }: { x: number; y: number; children: React.ReactNode; color?: string; size?: number }) => (
    <text x={x} y={y} fill={color} fontSize={size} fontWeight="900" textAnchor="middle">{children}</text>
  );
  const Meter = ({ x, y, label, value, color }: { x: number; y: number; label: string; value: number; color: string }) => (
    <>
      <text x={x} y={y - 10} fill="#cbd5e1" fontSize="13" fontWeight="900">{label}</text>
      <rect x={x} y={y} width="150" height="18" rx="9" fill="#172033" stroke="#334155" />
      <rect x={x} y={y} width={150 * Math.max(0.03, Math.min(1, value))} height="18" rx="9" fill={color} />
    </>
  );
  const Wall = ({ x, sponge = false }: { x: number; sponge?: boolean }) => (
    <rect x={x} y={sponge ? 178 : 158} width={sponge ? 58 : 34} height={sponge ? 136 : 156} rx={sponge ? 18 : 5} fill={sponge ? '#bae6fd' : '#64748b'} stroke={sponge ? '#38bdf8' : '#cbd5e1'} strokeWidth="4" />
  );

  if (title === 'Mass in Motion') {
    const grow = 0.6 + phase * 0.8;
    return (
      <>
        <path d="M110 318 L304 130 M610 318 L416 130" stroke="#475569" strokeWidth="5" />
        <Bicycle x={126 + phase * 88} y={205 + phase * 82} scale={grow} time={time} />
        <g transform={`translate(${430 - phase * 24} ${206 + phase * 82}) scale(${grow})`}>
          <rect x="-70" y="-32" width="140" height="56" rx="8" fill="#475569" />
          <rect x="-48" y="-58" width="96" height="30" rx="6" fill="#64748b" />
          <circle cx="-44" cy="34" r="14" fill="#111827" stroke="#e0f2fe" strokeWidth="4" />
          <circle cx="44" cy="34" r="14" fill="#111827" stroke="#e0f2fe" strokeWidth="4" />
        </g>
        <Meter x={76} y={108} label="Bike Stopping Difficulty" value={0.22} color="#22c55e" />
        <Meter x={470} y={108} label="Train Stopping Difficulty" value={1} color="#ef4444" />
        <Label x={360} y={82} color="#facc15">same fixed speed, very different momentum</Label>
      </>
    );
  }

  if (title === 'The Equation: p = mv') {
    const mass = Math.round(1000 + a * 9000);
    const velocity = Math.round(1 + b * 29);
    return (
      <>
        <Ground y={318} />
        <Car x={110 + phase * 450} y={286} scale={0.78 + a * 0.62} color="#64748b" />
        <Arrow x1="128" y1="210" x2={250 + b * 330} y2="210" color="#38bdf8" label={`${velocity} m/s`} width={6} />
        <Label x={360} y={96} color="#facc15" size={25}>{mass} × {velocity} = {mass * velocity} kg·m/s</Label>
        <Meter x={260} y={132} label="Momentum" value={(mass * velocity) / 300000} color="#f97316" />
      </>
    );
  }

  if (title === 'Direction Matters') {
    const wobble = Math.sin(phase * Math.PI * 2) * 14;
    return (
      <>
        <Ground y={314} />
        <Ball x={180 + wobble} y={282} color="#38bdf8" />
        <Ball x={540 - wobble} y={282} color="#38bdf8" />
        <Arrow x1="132" y1="210" x2="304" y2="210" color="#22c55e" label="+p = +50" width={6} />
        <Arrow x1="588" y1="248" x2="416" y2="248" color="#fb7185" label="-p = -50" width={6} />
        <Label x={360} y={102}>same speed, opposite signs</Label>
      </>
    );
  }

  if (title === 'The Physics of Stopping') {
    const sponge = selected === 1;
    const p = Math.min(1, phase * (sponge ? 0.75 : 1.8));
    const carX = sponge ? Math.min(484, 110 + p * 430) : Math.min(462, 110 + p * 650);
    const force = sponge ? 0.28 : p > 0.55 ? 1 : 0.18;
    return (
      <>
        <Ground y={314} /><Wall x={540} sponge={sponge} />
        <Car x={carX} y={282} color="#38bdf8" />
        {sponge && p > 0.62 && <ellipse cx="538" cy="248" rx={24 + Math.sin(p * Math.PI) * 24} ry="46" fill="rgba(56,189,248,0.35)" />}
        {!sponge && p > 0.55 && <path d="M505 205 L548 250 L510 296" stroke="#ef4444" strokeWidth="8" fill="none" />}
        <Meter x={244} y={108} label="Impact Force" value={force} color={force > 0.7 ? '#ef4444' : '#22c55e'} />
        <Label x={360} y={82} color={sponge ? '#22c55e' : '#ef4444'}>{sponge ? 'longer stop, lower force' : 'instant stop, red force spike'}</Label>
      </>
    );
  }

  if (title === 'Engineering Survival') {
    const p = Math.min(1, phase * 1.4);
    return (
      <>
        <Ground y={206} /><Ground y={330} />
        <Wall x={560} /><Wall x={560} sponge />
        <Car x={Math.min(470, 96 + p * 560)} y={174} color="#ef4444" />
        <Car x={Math.min(470, 96 + p * 480)} y={298} color="#38bdf8" />
        <circle cx={p > 0.58 ? 494 : 414} cy="138" r="12" fill="#facc15" />
        <ellipse cx="486" cy="262" rx={p > 0.58 ? 42 : 5} ry={p > 0.58 ? 34 : 5} fill="#e0f2fe" />
        <circle cx={p > 0.58 ? 456 : 414} cy="262" r="12" fill="#facc15" />
        <Meter x={68} y={92} label="A no airbag force" value={p > 0.58 ? 1 : 0.15} color="#ef4444" />
        <Meter x={68} y={238} label="B airbag force" value={p > 0.58 ? 0.28 : 0.15} color="#22c55e" />
      </>
    );
  }

  if (title === 'Rolling With The Punches') {
    const rolling = selected === 1;
    const p = Math.min(1, phase * 1.45);
    const targetX = rolling && p > 0.42 ? 438 + (p - 0.42) * 120 : 438;
    return (
      <>
        <ellipse cx={150 + p * 290} cy="230" rx="58" ry="34" fill="#ef4444" />
        <rect x={targetX - 28} y="150" width="56" height="156" rx="26" fill="#facc15" />
        {rolling && p > 0.42 && <Arrow x1={targetX - 10} y1="116" x2={targetX + 100} y2="116" color="#22c55e" label="target rolls back" width={6} />}
        <Meter x={88} y={106} label="Force on target" value={rolling ? 0.3 : p > 0.42 ? 0.95 : 0.16} color={rolling ? '#22c55e' : '#ef4444'} />
      </>
    );
  }

  if (title === 'The Accounting Rule of the Universe') {
    const before = phase < 0.48;
    const blueX = before ? 110 + phase * 450 : 326;
    const redX = before ? 430 : 430 + (phase - 0.48) * 455;
    return (
      <>
        <rect x="70" y="314" width="580" height="14" rx="7" fill="#bae6fd" />
        <rect x={blueX} y="252" width="86" height="52" rx="9" fill="#38bdf8" /><rect x={redX} y="252" width="86" height="52" rx="9" fill="#fb7185" />
        <Label x={360} y={94} color="#22c55e" size={20}>Total Momentum: constant before and after</Label>
        <Arrow x1="118" y1="218" x2="286" y2="218" color="#38bdf8" label="blue original speed" width={6} />
        {!before && <Arrow x1="446" y1="218" x2="614" y2="218" color="#fb7185" label="red gets that speed" width={6} />}
      </>
    );
  }

  if (title === 'Adding the Vectors') {
    const hit = phase > 0.52;
    const leftX = hit ? 310 : 100 + phase * 400;
    const rightX = hit ? 390 : 560 - phase * 330;
    return (
      <>
        <Ground y={314} />
        <Car x={leftX} y={282} color="#38bdf8" />
        <g transform={`translate(${rightX} 282) scale(-1 1)`}><Car x={0} y={0} color="#f97316" /></g>
        <Label x={185} y={130} color="#22c55e">+5000</Label><Label x={535} y={130} color="#fb7185">-5000</Label>
        <Label x={360} y={98} color="#facc15" size={22}>Total: 0</Label>
        {hit && <path d="M314 250 L360 286 L404 250" stroke="#ef4444" strokeWidth="8" fill="none" />}
      </>
    );
  }

  if (title === 'The Collision Math') {
    const p = started ? Math.min(1, phase * 1.4) : 0;
    const hit = p > 0.56;
    return (
      <>
        <Ground y={314} />
        <rect x={hit ? 322 : 116 + p * 370} y="252" width="76" height="48" rx="8" fill="#38bdf8" />
        <rect x={hit ? 398 + (p - 0.56) * 110 : 430} y="252" width="90" height="48" rx="8" fill="#f97316" />
        <Label x={154} y={238}>Cart A: 2kg, 5m/s</Label><Label x={475} y={238}>Cart B: 3kg, 0m/s</Label>
        <rect x="180" y="76" width="360" height="44" rx="12" fill="rgba(250,204,21,0.16)" stroke="#facc15" />
        <Label x={360} y={104} color="#facc15" size={20}>Total momentum before: 10 kg·m/s</Label>
      </>
    );
  }

  if (title === 'Solving for the Final Speed') {
    const p = Math.min(1, phase * 1.15);
    const x = p < 0.44 ? 118 + p * 420 : 304 + (p - 0.44) * 180;
    return (
      <>
        <Ground y={314} />
        <rect x={x} y="252" width={p > 0.44 ? 168 : 76} height="48" rx="8" fill="#38bdf8" />
        {p <= 0.44 && <rect x="430" y="252" width="90" height="48" rx="8" fill="#f97316" />}
        {p > 0.44 && <text x={x + 84} y="282" fill="#111827" fontSize="14" fontWeight="900" textAnchor="middle">locked 5kg cart</text>}
        <Label x={360} y={96} color="#facc15" size={24}>{p < 0.55 ? '10 = 5 × v' : 'v = 2 m/s'}</Label>
        <Arrow x1={x + 60} y1="218" x2={x + 170} y2="218" color="#22c55e" label="slower shared speed" width={6} />
      </>
    );
  }

  if (title === 'The Energy Difference') {
    const hit = phase > 0.5;
    const p = Math.min(1, phase * 1.4);
    return (
      <>
        <Ground y={314} />
        <Car x={hit ? 312 : 110 + p * 360} y={282} color="#38bdf8" />
        <g transform={`translate(${hit ? 404 : 585 - p * 330} 282) scale(-1 1)`}><Car x={0} y={0} color="#f97316" /></g>
        {hit && <path d="M314 246 L360 288 L410 246" stroke="#ef4444" strokeWidth="8" fill="none" />}
        <Meter x={90} y={100} label="Kinetic Energy" value={hit ? 0.32 : 1} color="#38bdf8" />
        <Meter x={90} y={154} label="Heat & Sound" value={hit ? 0.68 : 0.05} color="#ef4444" />
      </>
    );
  }

  if (title === 'The Perfect Bounce') {
    const hit = phase > 0.52;
    const x1 = hit ? 324 : 112 + phase * 405;
    const x2 = hit ? 416 + (phase - 0.52) * 410 : 420;
    return (
      <>
        <Ground y={314} />
        <Ball x={x1} y={282} color="#38bdf8" /><Ball x={x2} y={282} color="#f97316" />
        <Arrow x1={hit ? x2 - 10 : x1 + 20} y1="218" x2={hit ? x2 + 130 : x1 + 150} y2="218" color="#22c55e" label="same speed transfers" width={6} />
        <Meter x={250} y={102} label="Kinetic Energy" value={1} color="#22c55e" />
      </>
    );
  }

  if (title === 'The Perfectly Inelastic Crash') {
    const hit = phase > 0.46;
    const ballX = hit ? 350 + (phase - 0.46) * 125 : 392 + phase * 45;
    return (
      <>
        <Ground y={314} />
        <ellipse cx={hit ? ballX - 22 : 116 + phase * 520} cy="274" rx={hit ? 38 : 24} ry={hit ? 22 : 18} fill="#a855f7" />
        <Ball x={ballX} y={282} color="#64748b" />
        {hit && <path d={`M${ballX - 50} 266 Q${ballX - 10} 236 ${ballX + 34} 266`} stroke="#a855f7" strokeWidth="8" fill="none" />}
        <Label x={360} y={100} color="#facc15" size={22}>{hit ? 'Mass = m1 + m2' : 'Mass = m1'}</Label>
        {hit && <Arrow x1={ballX + 36} y1="220" x2={ballX + 104} y2="220" color="#22c55e" label="slower unified motion" width={5} />}
      </>
    );
  }

  if (title === 'The Ballistic Pendulum') {
    const p = Math.min(1, phase * 1.25);
    const embedded = p > 0.36;
    const swing = Math.max(0, p - 0.36) / 0.64;
    const blockX = 420 + Math.sin(swing * Math.PI * 0.9) * 90;
    const blockY = 190 - Math.sin(swing * Math.PI) * 76;
    return (
      <>
        <line x1="420" y1="82" x2={blockX} y2={blockY} stroke="#e0f2fe" strokeWidth="5" />
        <rect x={blockX - 48} y={blockY} width="96" height="70" rx="10" fill="#a16207" />
        <circle cx={embedded ? blockX - 30 : 80 + p * 900} cy={embedded ? blockY + 34 : 224} r="8" fill="#facc15" />
        <path d="M468 272 Q520 215 510 146" fill="none" stroke="#38bdf8" strokeWidth="4" strokeDasharray="7 7" />
        <Label x={360} y={342} color="#facc15">height to energy to momentum: Calculated bullet speed: 400 m/s</Label>
      </>
    );
  }

  if (title === 'Zero Initial Momentum') {
    const fired = started;
    const p = fired ? Math.min(1, phase * 1.2) : 0;
    return (
      <>
        <Ground y={318} />
        <rect x={180 - p * 95} y="258" width="130" height="48" rx="8" fill="#475569" /><circle cx={282 - p * 95} cy="282" r="24" fill="#111827" stroke="#e0f2fe" strokeWidth="5" />
        <circle cx={fired ? 312 + p * 270 : 274} cy="252" r="20" fill="#facc15" />
        <Label x={360} y={96} color="#22c55e" size={22}>Total Momentum: 0</Label>
        {fired && <><Arrow x1="306" y1="224" x2="150" y2="224" color="#fb7185" label="-p cannon" width={6} /><Arrow x1="390" y1="198" x2="548" y2="198" color="#22c55e" label="+p ball" width={6} /></>}
      </>
    );
  }

  if (title === 'Rocket Propulsion Revisited') {
    const fired = started;
    const p = fired ? Math.min(1, phase * 1.25) : 0;
    return (
      <>
        <rect x="54" y="76" width="612" height="266" rx="18" fill="#020617" />
        {Array.from({ length: 26 }).map((_, i) => <circle key={i} cx={80 + ((i * 83) % 560)} cy={98 + ((i * 47) % 215)} r="2" fill="#e0f2fe" opacity="0.75" />)}
        <g transform={`translate(${330 + p * 130} ${232 - p * 45}) rotate(90)`}><polygon points="0,-70 -34,10 34,10" fill="#e0f2fe" /><rect x="-26" y="8" width="52" height="70" rx="12" fill="#94a3b8" /></g>
        {fired && <path d={`M${298 + p * 130} ${232 - p * 45} C210 272,160 220,92 286`} fill="none" stroke="#fb7185" strokeWidth="14" strokeLinecap="round" />}
        <Label x={360} y={102} color="#22c55e">p exhaust backward = p rocket forward</Label>
      </>
    );
  }

  if (title === 'The Deadly Bounce') {
    const p = phase;
    const bounced = p > 0.55;
    return (
      <>
        <Ground y={200} /><Ground y={330} /><Wall x={548} /><Wall x={548} />
        <Car x={bounced ? 470 - (p - 0.55) * 320 : 116 + p * 620} y={168} color="#94a3b8" />
        <Car x={p > 0.55 ? 466 : 116 + p * 620} y={298} color="#38bdf8" />
        <Meter x={78} y={78} label="Titanium bounce Δp" value={1} color="#ef4444" />
        <Meter x={78} y={238} label="Crumple stop Δp" value={0.5} color="#f97316" />
        <Label x={612} y={104} color="#ef4444">2x</Label><Label x={612} y={264} color="#f97316">1x</Label>
      </>
    );
  }

  if (title === "The Seatbelt's Job") {
    const p = Math.min(1, phase * 1.1);
    const carX = Math.min(438, 110 + p * 420);
    const crumple = p > 0.62 ? (p - 0.62) * 90 : 0;
    return (
      <>
        <Ground y={314} /><Wall x={550} />
        <g transform={`translate(${carX} 282)`}>
          <rect x="-62" y="-45" width={118 - crumple} height="48" rx="9" fill="#38bdf8" />
          <circle cx="-22" cy="-56" r="12" fill="#facc15" />
          <path d="M-42 -70 L10 -28" stroke="#f8fafc" strokeWidth="8" strokeLinecap="round" />
          <path d="M18 -36 L46 -12" stroke="#ef4444" strokeWidth="6" strokeLinecap="round" />
        </g>
        <Label x={360} y={100} color="#22c55e">crumple zone and seatbelt stretch the stop together</Label>
      </>
    );
  }

  if (title === 'The Single Balance Point' || title === 'Multi-Object Systems') {
    const p = phase;
    const cx = 100 + p * 520;
    const cy = 304 - 230 * p + 230 * p * p;
    return title === 'The Single Balance Point' ? (
      <>
        <path d="M100 304 Q360 74 620 304" fill="none" stroke="#22c55e" strokeWidth="4" strokeDasharray="8 8" />
        <g transform={`translate(${cx} ${cy}) rotate(${time * 9})`}>
          <rect x="-72" y="-10" width="144" height="20" rx="10" fill="#94a3b8" />
          <circle cx="-44" cy="0" r="28" fill="#64748b" />
          <circle cx="-28" cy="-2" r="8" fill="#22c55e" />
        </g>
        <Label x={360} y={96}>green center of mass follows the clean parabola</Label>
      </>
    ) : (
      <>
        <path d="M100 304 Q360 74 620 304" fill="none" stroke="#22c55e" strokeWidth="4" strokeDasharray="8 8" />
        {Array.from({ length: 18 }).map((_, i) => <circle key={i} cx={cx + Math.cos(i * 1.7) * Math.max(0, p - 0.42) * 170} cy={cy + Math.sin(i * 2.1) * Math.max(0, p - 0.42) * 116} r="5" fill={i % 2 ? '#facc15' : '#fb7185'} />)}
        <path d={`M${cx - 14} ${cy} H${cx + 14} M${cx} ${cy - 14} V${cy + 14}`} stroke="#22c55e" strokeWidth="5" />
        <Label x={360} y={96}>average position ignores the explosion scatter</Label>
      </>
    );
  }

  if (title === 'Symmetry vs Asymmetry') {
    const fulcrumX = 250 + a * 240;
    const balanced = a > 0.68;
    return (
      <>
        <polygon points={`${fulcrumX - 22},318 ${fulcrumX + 22},318 ${fulcrumX},250`} fill="#facc15" />
        <g transform={`rotate(${balanced ? 0 : -18},${fulcrumX},248)`}><rect x="150" y="232" width="390" height="30" rx="15" fill="#a16207" /><ellipse cx="500" cy="247" rx="64" ry="34" fill="#78350f" /></g>
        <Label x={360} y={104} color={balanced ? '#22c55e' : '#facc15'}>{balanced ? 'fulcrum near barrel balances the bat' : 'geometric center is not the balance point'}</Label>
      </>
    );
  }

  if (title === 'The Plumb Line Test') {
    const stepTwo = selected === 1 && started;
    return (
      <>
        <polygon points="250,118 430,90 520,198 468,318 300,292 198,210" fill="#475569" stroke="#e0f2fe" strokeWidth="4" />
        {!started && <Label x={360} y={342} color="#facc15">Drop the first plumb line, then rotate and pin again</Label>}
        {started && <><circle cx="250" cy="118" r="8" fill="#facc15" /><line x1="250" y1="118" x2="250" y2="330" stroke="#ef4444" strokeWidth="5" /></>}
        {stepTwo && <><circle cx="520" cy="198" r="8" fill="#facc15" /><line x1="520" y1="198" x2="250" y2="330" stroke="#38bdf8" strokeWidth="5" /><Label x={250} y={352} color="#facc15" size={22}>star: center of mass</Label></>}
      </>
    );
  }

  if (title === 'Torque: The Rotational Push') {
    const distance = 0.12 + a * 0.88;
    const pushX = 184 + distance * 330;
    return (
      <>
        <circle cx="184" cy="204" r="18" fill="#facc15" />
        <g transform={`rotate(${-8 - distance * 42},184,204)`}><rect x="184" y="170" width="330" height="68" rx="8" fill="#7c2d12" /></g>
        <Arrow x1={pushX} y1="146" x2={pushX} y2="236" color="#22c55e" label="10N" width={6} />
        <Label x={360} y={94} color="#facc15" size={22}>Torque = Force × Distance = 10 × {distance.toFixed(2)}</Label>
      </>
    );
  }

  if (title === 'Rotational Equilibrium') {
    const rightX = 360 + 70 + a * 180;
    const balanced = Math.abs(rightX - 500) < 24;
    return (
      <>
        <polygon points="340,318 380,318 360,248" fill="#facc15" />
        <g transform={`rotate(${balanced ? 0 : -14},360,248)`}><rect x="120" y="238" width="480" height="18" rx="9" fill="#94a3b8" /><Box x="190" y="166" color="#f97316" label="50kg" /><Box x={rightX - 40} y="166" color="#38bdf8" label="25kg" /></g>
        <Label x={360} y={96} color={balanced ? '#22c55e' : '#facc15'}>{balanced ? 'balanced at double the distance' : 'drag 25kg farther from center'}</Label>
      </>
    );
  }

  if (title === 'The Base of Support') {
    const over = a > 0.72;
    return (
      <>
        <Ground y={330} /><rect x="305" y="326" width="110" height="6" rx="3" fill="#facc15" />
        <g transform={`rotate(${over ? -34 : -a * 24},360,314)`}>
          <rect x="310" y="126" width="100" height="190" rx="10" fill="#f97316" />
          <circle cx="360" cy="170" r="10" fill="#22c55e" />
          <Arrow x1="360" y1="186" x2="360" y2="330" color={over ? '#ef4444' : '#22c55e'} label="gravity" width={4} />
        </g>
        <Label x={360} y={96} color={over ? '#ef4444' : '#22c55e'}>{over ? 'gravity line outside footprint: tips' : 'gravity line inside footprint: returns'}</Label>
      </>
    );
  }

  if (title === 'High vs Low Centers') {
    const truckTips = a > 0.48;
    return (
      <>
        <path d="M95 312 C190 120, 530 120, 625 312" fill="none" stroke="#64748b" strokeWidth="22" />
        <g transform={`translate(218 270) rotate(${a * 6})`}><Car x={0} y={0} scale={0.82} color="#38bdf8" /><circle cx="56" cy="-42" r="8" fill="#22c55e" /></g>
        <g transform={`translate(500 274) rotate(${truckTips ? -24 : -a * 12})`}><rect x="-54" y="-126" width="108" height="126" rx="9" fill="#f97316" /><circle cx="0" cy="-96" r="9" fill="#22c55e" /><rect x="-60" y="0" width="120" height="30" rx="8" fill="#64748b" /></g>
        <Label x={360} y={94} color={truckTips ? '#ef4444' : '#facc15'}>Turn Speed: {Math.round(a * 100)}% - truck tips first, sports car stays stable</Label>
      </>
    );
  }

  return null;
};

const CalibrationScene = ({
  spec,
  selected,
  slider,
  sliderB = 45,
  actionCount,
  time,
}: {
  spec: SimulationSpec;
  selected: number;
  slider: number;
  sliderB?: number;
  actionCount: number;
  time: number;
}) => {
  const phase = (time % 100) / 100;
  const title = spec.title;
  const sliderValue = slider / 100;
  const sliderBValue = sliderB / 100;

  if (spec.gradeLevel === 9) {
    return <Grade9Scene spec={spec} selected={selected} slider={slider} sliderB={sliderB} actionCount={actionCount} time={time} />;
  }

  if (spec.gradeLevel === 10) {
    return <Grade10Scene spec={spec} selected={selected} slider={slider} sliderB={sliderB} actionCount={actionCount} time={time} />;
  }

  if (title === 'Pushes and Pulls') {
    const push = selected === 0;
    const progress = Math.sin(phase * Math.PI);
    const cartX = 300 + (push ? progress * 230 : 0);
    const dogX = 420 - (!push ? progress * 170 : 0);
    return (
      <>
        <Ground y={318} />
        {push ? (
          <>
            <Person x={180 + progress * 120} y={160} />
            <rect x={cartX} y="238" width="110" height="58" rx="10" fill="#67e8f9" />
            <circle cx={cartX + 22} cy="306" r="14" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
            <circle cx={cartX + 88} cy="306" r="14" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
            <line x1={224 + progress * 120} y1="214" x2={cartX} y2="252" stroke="#e0f2fe" strokeWidth="8" strokeLinecap="round" />
            <Arrow x1={cartX - 80} y1="198" x2={cartX + 110} y2="198" color="#22c55e" label="push makes cart roll away" width={7} />
          </>
        ) : (
          <>
            <circle cx="610" cy="270" r="18" fill="#fbbf24" />
            <text x="610" y="234" fill="#fde68a" fontSize="13" fontWeight="900" textAnchor="middle">squirrel</text>
            <g transform={`translate(${dogX} 270)`}>
              <ellipse cx="0" cy="0" rx="44" ry="24" fill="#f59e0b" />
              <circle cx="42" cy="-12" r="18" fill="#f59e0b" />
              <line x1="-24" y1="18" x2="-34" y2="44" stroke="#f59e0b" strokeWidth="7" strokeLinecap="round" />
              <line x1="22" y1="18" x2="34" y2="44" stroke="#f59e0b" strokeWidth="7" strokeLinecap="round" />
            </g>
            <Person x={170} y={160} />
            <line x1="212" y1="214" x2={dogX - 42} y2="258" stroke="#e0f2fe" strokeWidth="5" strokeLinecap="round" strokeDasharray="10 6" />
            <Arrow x1={dogX - 10} y1="206" x2={dogX - 150} y2="206" color="#38bdf8" label="pull stops the chase" width={7} />
          </>
        )}
      </>
    );
  }

  if (title === 'Balanced vs. Unbalanced') {
    const uneven = selected === 1;
    const p = uneven ? Math.min(1, phase * 1.6) : 0;
    const boxX = 382 + Math.max(0, p - 0.35) * 170;
    return (
      <>
        <Ground y={302} />
        {uneven ? (
          <>
            <Person x={142 + p * 88} y={154} />
            <Person x={230 + p * 76} y={154} />
            <Box x={boxX} y={230} color="#7dd3fc" label="box" />
            <Arrow x1="250" y1="192" x2={boxX + 40} y2="192" color="#22c55e" label="unbalanced push: box slides" width={7} />
          </>
        ) : (
          <>
            <Person x={178} y={154} />
            <rect x="448" y="166" width="54" height="136" rx="5" fill="#b45309" />
            {Array.from({ length: 5 }).map((_, row) => Array.from({ length: 2 }).map((__, col) => (
              <rect key={`${row}-${col}`} x={452 + col * 24} y={172 + row * 25} width="20" height="20" fill="#92400e" stroke="#fbbf24" strokeWidth="1" />
            )))}
            <Arrow x1="224" y1="224" x2="422" y2="224" color="#fb7185" label="push" width={6} />
            <Arrow x1="502" y1="248" x2="306" y2="248" color="#38bdf8" label="equal push-back" width={6} />
            <text x="360" y="112" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">balanced forces: nothing moves</text>
          </>
        )}
      </>
    );
  }

  if (title === 'The Invisible Pull') {
    const fallY = phase < 0.22 ? 130 : 130 + ((phase - 0.22) / 0.78) * 196;
    return (
      <>
        <Ground y={332} rough />
        <rect x="168" y="118" width="32" height="214" rx="13" fill="#854d0e" />
        <ellipse cx="242" cy="104" rx="136" ry="56" fill="#166534" />
        <circle cx="360" cy={fallY} r="22" fill="#ef4444" />
        <path d={`M360 ${fallY - 20} C348 ${fallY - 44}, 386 ${fallY - 42}, 372 ${fallY - 20}`} fill="#22c55e" />
        <Arrow x1={360} y1={fallY + 32} x2={360} y2={fallY + 90} color="#60a5fa" label="gravity pulls down" width={4} />
      </>
    );
  }

  if (title === 'Weight vs. Mass') {
    const moon = selected === 1;
    return (
      <>
        <Ground y={330} />
        <Person x={250} y={126} />
        <circle cx="512" cy="212" r="72" fill={moon ? '#cbd5e1' : '#38bdf8'} opacity="0.9" />
        <text x="512" y="214" fill="#082f49" fontSize="24" fontWeight="900" textAnchor="middle">{moon ? 'Moon' : 'Earth'}</text>
        <text x="250" y="78" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">mass: 70 kg</text>
        <text x="250" y="312" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">weight: {moon ? '114 N' : '686 N'}</text>
        <Arrow x1={250} y1={248} x2={250} y2={moon ? 288 : 320} color="#facc15" label="weight" width={moon ? 3 : 7} />
      </>
    );
  }

  if (title === 'The Invisible Brake Pedal') {
    const p = Math.min(1, phase * 1.28);
    const x = 112 + (1 - Math.pow(1 - p, 2)) * 430;
    const motionWidth = Math.max(0, 152 * (1 - p));
    return (
      <>
        <Ground y={302} rough />
        <Ball x={x} y={270} color="#93c5fd" />
        {motionWidth > 12 && <Arrow x1={x - 58} y1={212} x2={x - 58 + motionWidth} y2={212} color="#22c55e" label="motion slows" width={4 + motionWidth / 32} />}
        <Arrow x1={x + 58} y1={326} x2={x - 58} y2={326} color="#fb7185" label="friction" width={4} />
      </>
    );
  }

  if (title === 'Braking vs. Pedaling') {
    const braking = selected === 1;
    const p = braking ? Math.min(1, phase * 1.5) : phase;
    const x = braking ? 122 + (1 - Math.pow(1 - p, 2)) * 340 : 96 + ((time * 3.2) % 520);
    return (
      <>
        <Ground y={314} />
        <Bicycle x={x} y={236} braking={braking} pedaling={!braking} time={time} />
        {!braking && <Arrow x1={x - 92} y1={186} x2={x + 110} y2={186} color="#22c55e" label="pedaling force speeds up" width={7} />}
        {braking && <Arrow x1={x + 92} y1={310} x2={x - 62} y2={310} color="#fb7185" label="brake force at wheel" width={7} />}
      </>
    );
  }

  if (title === 'Smooth vs. Rough') {
    const rough = selected === 1;
    const stopPhase = rough ? Math.min(phase * 1.4, 1) : phase;
    const x = rough ? 120 + stopPhase * 350 : 90 + ((time * 3.2) % 530);
    const arrow = rough ? Math.max(0, 146 * (1 - stopPhase)) : 154;
    return (
      <>
        <Ground y={302} rough={rough} />
        <Ball x={x} y={270} color={rough ? '#fbbf24' : '#60a5fa'} />
        {arrow > 12 && <Arrow x1={x - 62} y1={212} x2={x - 62 + arrow} y2={212} color="#22c55e" label="motion" width={rough ? 4 + (arrow / 154) * 4 : 8} />}
        {rough && <Arrow x1={x + 54} y1={326} x2={x - 42} y2={326} color="#fb7185" label="rough surface" width={4} />}
      </>
    );
  }

  if (title === 'The Rule of Laziness') {
    const wallAdded = selected === 1;
    const rockX = wallAdded ? Math.min(426, 94 + phase * 520) : 86 + ((time * 1.15) % 540);
    return (
      <>
        <rect x="42" y="78" width="636" height="248" rx="18" fill="#020617" stroke="rgba(148,163,184,0.2)" />
        {Array.from({ length: 28 }).map((_, index) => (
          <circle key={index} cx={64 + ((index * 89) % 590)} cy={92 + ((index * 47) % 208)} r="1.7" fill="#e0f2fe" opacity="0.55" />
        ))}
        {wallAdded && <rect x="470" y="122" width="28" height="178" rx="8" fill="#94a3b8" />}
        <ellipse cx={rockX} cy="218" rx="34" ry="25" fill="#a3a3a3" />
        {!wallAdded && <Arrow x1={rockX - 84} y1="174" x2={rockX + 58} y2="174" color="#38bdf8" label="constant drift" width={4} />}
        {!wallAdded && <text x="360" y="352" fill="#e0f2fe" fontSize="17" fontWeight="900" textAnchor="middle">no force: same speed, same direction</text>}
        {wallAdded && rockX >= 424 && <text x="360" y="354" fill="#facc15" fontSize="18" fontWeight="900" textAnchor="middle">wall changes the motion</text>}
      </>
    );
  }

  if (title === 'Miles Per Hour') {
    const carX = 96 + ((time * 3.8) % 520);
    const elapsed = Math.floor(phase * 60);
    const miles = Math.floor(phase * 60);
    return (
      <>
        <rect x="58" y="286" width="604" height="58" rx="8" fill="#334155" />
        <line x1="78" y1="315" x2="642" y2="315" stroke="#e5e7eb" strokeWidth="4" strokeDasharray="34 24" />
        <Car x={carX} y={284} color="#38bdf8" />
        <circle cx="552" cy="132" r="58" fill="#020617" stroke="#7dd3fc" strokeWidth="5" />
        <text x="552" y="124" fill="#e0f2fe" fontSize="16" fontWeight="900" textAnchor="middle">speed</text>
        <text x="552" y="154" fill="#facc15" fontSize="28" fontWeight="900" textAnchor="middle">60 mph</text>
        <text x="174" y="112" fill="#e0f2fe" fontSize="19" fontWeight="900" textAnchor="middle">clock: 0:{String(elapsed).padStart(2, '0')}</text>
        <text x="174" y="142" fill="#facc15" fontSize="19" fontWeight="900" textAnchor="middle">odometer: {miles} mi</text>
        <text x="360" y="366" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">60 miles ÷ 1 hour = 60 mph</text>
      </>
    );
  }

  if (title === 'More Than Just Fast') {
    return (
      <>
        <rect x="110" y="128" width="190" height="120" rx="18" fill="rgba(14,165,233,0.14)" stroke="#38bdf8" />
        <text x="205" y="178" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">Speed</text>
        <text x="205" y="214" fill="#facc15" fontSize="30" fontWeight="900" textAnchor="middle">30 mph</text>
        <Arrow x1="398" y1="190" x2="598" y2="190" color="#22c55e" label="direction" width={8} />
      </>
    );
  }

  if (title === 'Why Does Direction Matter?') {
    const headwind = phase > 0.48;
    const planeX = headwind ? 96 + 260 + Math.sin(time / 8) * 18 : 96 + phase * 520;
    return (
      <>
        <rect x="42" y="80" width="636" height="230" rx="18" fill="#0f2742" />
        <g transform={`translate(${planeX} 194)`}>
          <polygon points="-54,0 42,-18 70,0 42,18" fill="#e0f2fe" />
          <polygon points="-10,0 -42,-42 20,-8" fill="#93c5fd" />
          <polygon points="-10,0 -42,42 20,8" fill="#93c5fd" />
        </g>
        <Arrow x1="90" y1="114" x2={headwind ? 238 : 330} y2="114" color="#22c55e" label={headwind ? 'slowing' : 'fast flight'} width={headwind ? 4 : 8} />
        {headwind && Array.from({ length: 5 }).map((_, index) => <Arrow key={index} x1={610 - index * 38} y1={154 + index * 22} x2={492 - index * 26} y2={154 + index * 12} color="#facc15" label={index === 0 ? 'headwind' : undefined} width={4} />)}
      </>
    );
  }

  if (title === 'Relative Speed') {
    const sameSpeed = selected === 1;
    const carX = sameSpeed ? 350 + Math.sin(time / 9) * 26 : 120 + ((time * 4.6) % 470);
    return (
      <>
        <rect x="58" y="280" width="604" height="60" rx="8" fill="#334155" />
        <Car x={carX} y={276} scale={sameSpeed ? 0.95 : 1} color="#38bdf8" />
        <text x="360" y="120" fill="#e0f2fe" fontSize="22" fontWeight="900" textAnchor="middle">{sameSpeed ? "your speed = car's speed" : 'your speed = 0'}</text>
        <Arrow x1="150" y1="174" x2={sameSpeed ? 238 : 390} y2="174" color="#22c55e" label={sameSpeed ? 'barely moves relative to you' : 'speeds away'} width={sameSpeed ? 3 : 8} />
      </>
    );
  }

  if (title === 'The Head-On Collision') {
    const p = phase;
    const aX = 112 + p * 500;
    const bX = 608 - p * 500;
    return (
      <>
        <Ground y={312} />
        <Car x={aX} y={282} color="#38bdf8" />
        <g transform={`scale(-1 1) translate(${-bX} 282)`}><Car x={0} y={0} color="#f97316" /></g>
        <Arrow x1={aX - 76} y1={208} x2={aX + 74} y2={208} color="#22c55e" label="60 mph" width={5} />
        <Arrow x1={bX + 76} y1={238} x2={bX - 74} y2={238} color="#f97316" label="60 mph" width={5} />
        {Math.abs(aX - bX) < 64 && <text x="360" y="124" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">60 + 60 = 120 mph closing speed</text>}
      </>
    );
  }

  if (title === 'The Feeling of Acceleration') {
    const speed = sliderValue;
    const lean = (speed - 0.5) * 34;
    return (
      <>
        <rect x="58" y="286" width="604" height="58" rx="8" fill="#334155" />
        <rect x="246" y="202" width="222" height="78" rx="18" fill="#38bdf8" />
        <rect x="282" y="152" width="120" height="58" rx="16" fill="rgba(224,242,254,0.65)" />
        <circle cx="294" cy="290" r="18" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <circle cx="420" cy="290" r="18" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <g transform={`rotate(${lean},350,202)`} stroke="#111827" strokeWidth="7" strokeLinecap="round" fill="none">
          <circle cx="350" cy="174" r="16" fill="#fde68a" stroke="none" />
          <line x1="350" y1="192" x2="350" y2="238" />
          <line x1="350" y1="238" x2="330" y2="264" />
          <line x1="350" y1="238" x2="376" y2="260" />
        </g>
        <Arrow x1="112" y1="142" x2={112 + 82 + speed * 180} y2="142" color={speed > 0.5 ? '#22c55e' : '#fb7185'} label={speed > 0.5 ? 'accelerating' : 'braking'} width={5 + speed * 5} />
        <text x="560" y="144" fill="#facc15" fontSize="26" fontWeight="900" textAnchor="middle">{Math.round(speed * 80)} mph</text>
      </>
    );
  }

  if (title === 'Turning is Accelerating') {
    const angle = time / 18;
    const carX = 360 + Math.cos(angle) * 150;
    const carY = 214 + Math.sin(angle) * 86;
    return (
      <>
        <ellipse cx="360" cy="214" rx="190" ry="112" fill="none" stroke="#64748b" strokeWidth="30" />
        <ellipse cx="360" cy="214" rx="190" ry="112" fill="none" stroke="#e5e7eb" strokeWidth="4" strokeDasharray="20 18" />
        <g transform={`rotate(${angle * 57.3 + 90}, ${carX}, ${carY})`}>
          <Car x={carX} y={carY} scale={0.85} color="#38bdf8" />
        </g>
        <Arrow x1={carX} y1={carY} x2={360} y2={214} color="#facc15" label="turning acceleration" width={4} />
      </>
    );
  }

  if (title === 'The Magic Beam') {
    const balanced = selected === 1;
    const angle = balanced ? 0 : -13;
    const heavyX = balanced ? 284 : 174;
    return (
      <>
        <polygon points="340,318 380,318 360,238" fill="#facc15" />
        <g transform={`rotate(${angle},360,238)`}>
          <rect x="144" y="228" width="432" height="18" rx="9" fill="#94a3b8" />
          <Box x={heavyX} y="156" color="#f97316" label="heavy" />
          <Box x="494" y="120" color="#7dd3fc" label="light" />
        </g>
        <Arrow x1="198" y1="94" x2={balanced ? 296 : 198} y2="94" color="#22c55e" label="move heavy box toward fulcrum" width={balanced ? 5 : 0.1} />
        <text x="360" y="82" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{balanced ? 'balanced: boxes end level' : 'imbalanced: light box high'}</text>
      </>
    );
  }

  if (title === 'Trading Distance for Strength') {
    const lift = Math.min(150, actionCount * 22);
    return (
      <>
        <polygon points="340,320 380,320 360,244" fill="#facc15" />
        <line x1="138" y1={280 + Math.sin(time / 5) * 4} x2="582" y2={214 - lift} stroke="#94a3b8" strokeWidth="16" strokeLinecap="round" />
        <Box x="500" y={166 - lift} color="#f97316" label="load" />
        <Arrow x1="188" y1={236 + Math.sin(time / 4) * 16} x2="188" y2={302 + Math.sin(time / 4) * 16} color="#22c55e" label="press" width={5} />
        <text x="360" y="88" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">presses add up: lift {actionCount}</text>
      </>
    );
  }

  if (title === 'Defeating the Ground') {
    const p = phase < 0.5 ? phase * 2 : (phase - 0.5) * 2;
    const wheeled = phase >= 0.5;
    const crateX = 126 + p * (wheeled ? 420 : 230);
    return (
      <>
        <Ground y={330} />
        {wheeled && <rect x={crateX - 12} y="286" width="110" height="18" rx="9" fill="#475569" />}
        {wheeled && <circle cx={crateX + 8} cy="314" r="14" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />}
        {wheeled && <circle cx={crateX + 78} cy="314" r="14" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />}
        <Box x={crateX} y={wheeled ? 222 : 238 + Math.sin(time / 3) * 4} color="#a16207" label="crate" />
        <Person x={crateX - 90} y={166} />
        <Arrow x1={crateX - 44} y1="218" x2={crateX + 70} y2="218" color="#22c55e" label="same pull" width={5} />
        {!wheeled && <Arrow x1={crateX + 72} y1="332" x2={crateX - 60} y2="332" color="#fb7185" label="large friction" width={8} />}
        {wheeled && <Arrow x1={crateX + 72} y1="332" x2={crateX + 14} y2="332" color="#fb7185" label="tiny friction" width={3} />}
        <text x="360" y="88" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{wheeled ? 'wheels stop the crate scraping the ground' : 'dragging scrapes: hard work'}</text>
      </>
    );
  }

  if (title === 'The Wheel and Axle') {
    const machine = selected === 1 || actionCount > 0;
    return (
      <>
        <g transform={`rotate(${time * 4},250,214)`}>
          <circle cx="250" cy="214" r="76" fill="none" stroke="#38bdf8" strokeWidth="16" />
          <line x1="174" y1="214" x2="326" y2="214" stroke="#38bdf8" strokeWidth="8" />
          <line x1="250" y1="138" x2="250" y2="290" stroke="#38bdf8" strokeWidth="8" />
        </g>
        {machine && (
          <>
            <rect x="438" y="178" width="40" height="124" rx="12" fill="#facc15" />
            <line x1="250" y1="214" x2="458" y2="240" stroke="#facc15" strokeWidth="10" strokeLinecap="round" />
            <Arrow x1="500" y1="208" x2="610" y2="208" color="#22c55e" label="axle turns load" width={5} />
          </>
        )}
        <text x="360" y="88" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">{machine ? 'simple machine: wheel + axle' : 'simple wheel: spins freely'}</text>
      </>
    );
  }

  if (title === 'The Inclined Plane') {
    const ramp = selected === 1;
    const boxX = ramp ? 190 + Math.min(1, phase * 1.4) * 290 : 330 + Math.sin(time / 5) * 10;
    const boxY = ramp ? 292 - Math.min(1, phase * 1.4) * 116 : 238 + Math.sin(time / 4) * 26;
    return (
      <>
        <rect x="510" y="162" width="74" height="168" rx="8" fill="#475569" />
        {ramp && <polygon points="190,330 510,330 510,174" fill="#64748b" />}
        <Box x={boxX} y={boxY} color="#7dd3fc" label="box" />
        {!ramp && (
          <>
            <rect x="300" y="110" width="120" height="22" rx="11" fill="#facc15" />
            <Arrow x1="360" y1="146" x2="360" y2="218" color="#fb7185" label="lift slips" width={5} />
          </>
        )}
        {ramp && <Arrow x1="196" y1="306" x2="404" y2="208" color="#22c55e" label="push up ramp" width={5} />}
      </>
    );
  }

  if (title === 'The Distance Trade-Off') {
    const steep = slider / 100;
    const topY = 290 - steep * 150;
    const boxX = 185 + phase * 300;
    const boxY = 290 - phase * (290 - topY) - 44;
    return (
      <>
        <rect x="500" y={topY} width="82" height={330 - topY} fill="#475569" />
        <polygon points={`150,330 500,330 500,${topY}`} fill="#64748b" />
        <Box x={boxX} y={boxY} color="#7dd3fc" label="box" />
        <Arrow x1="120" y1="112" x2={120 + 70 + steep * 170} y2="112" color="#f97316" label="effort" width={4 + steep * 6} />
        <text x="360" y="82" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{steep > 0.62 ? 'steeper ramp: harder push' : 'gentler ramp: longer, easier push'}</text>
      </>
    );
  }

  if (title === 'A Wheel With A Rope') {
    const pull = Math.sin(time / 12) * 34;
    const loadY = 270 - Math.max(0, pull + 30);
    return (
      <>
        <circle cx="360" cy="132" r="56" fill="none" stroke="#38bdf8" strokeWidth="14" />
        <line x1="304" y1="132" x2="214" y2={284 + pull} stroke="#e0f2fe" strokeWidth="8" strokeLinecap="round" />
        <line x1="416" y1="132" x2="506" y2={loadY} stroke="#e0f2fe" strokeWidth="8" strokeLinecap="round" />
        <Box x="464" y={loadY} color="#f97316" label="load" />
        <Arrow x1="214" y1={244 + pull} x2="214" y2={314 + pull} color="#22c55e" label="pull rope" width={5} />
      </>
    );
  }

  if (title === 'Multiplying Strength') {
    const pulleyCount = selected === 2 ? 4 : selected === 1 ? 2 : 1;
    const lift = 82;
    const pullDistance = lift * pulleyCount;
    return (
      <>
        <line x1="100" y1="82" x2="620" y2="82" stroke="#64748b" strokeWidth="16" strokeLinecap="round" />
        {Array.from({ length: pulleyCount }).map((_, index) => (
          <circle key={index} cx={230 + index * 72} cy={140 + (index % 2) * 66} r="32" fill="none" stroke="#38bdf8" strokeWidth="10" />
        ))}
        <rect x="436" y={254 - lift} width="124" height="72" rx="10" fill="#7c2d12" />
        <text x="498" y={296 - lift} fill="#fed7aa" fontSize="16" fontWeight="900" textAnchor="middle">piano</text>
        <path d={`M150 330 C190 ${260 - pullDistance * 0.3}, 250 122, ${230 + (pulleyCount - 1) * 72} ${140 + ((pulleyCount - 1) % 2) * 66} L498 ${254 - lift}`} fill="none" stroke="#e0f2fe" strokeWidth="7" strokeLinecap="round" />
        <Arrow x1="150" y1="260" x2="150" y2={330} color="#22c55e" label={`pull ${pulleyCount}x rope`} width={5} />
        <text x="360" y="366" fill="#facc15" fontSize="18" fontWeight="900" textAnchor="middle">force needed: {pulleyCount === 1 ? '100%' : pulleyCount === 2 ? '50%' : '25%'}</text>
      </>
    );
  }

  if (title === 'Energy in Action and in Waiting') {
    const fired = actionCount > 0;
    const pull = fired ? 0 : sliderValue;
    const launch = fired ? Math.min(1, phase * 1.6) : 0;
    const rockX = fired ? 292 + launch * 320 : 248 - pull * 76;
    const rockY = fired ? 254 - Math.sin(launch * Math.PI) * 138 : 236 + pull * 32;
    return (
      <>
        <Ground y={326} />
        <line x1="250" y1="286" x2={260 - pull * 88} y2={194 + pull * 58} stroke="#facc15" strokeWidth="12" strokeLinecap="round" />
        <circle cx={rockX} cy={rockY} r="18" fill="#a3a3a3" />
        <EnergyBar x={468} y={116} label="Potential" value={fired ? 0 : pull} color="#38bdf8" />
        <EnergyBar x={468} y={162} label="Kinetic" value={fired ? Math.max(0, 1 - launch * 0.65) : 0} color="#ef4444" />
        <text x="245" y="356" fill="#e0f2fe" fontSize="15" fontWeight="900" textAnchor="middle">{fired ? 'release: stored energy becomes motion' : 'drag Pull Back, then release'}</text>
      </>
    );
  }

  if (title === "Gravity's Battery") {
    const height = sliderValue;
    const dropping = selected === 1;
    const fall = dropping ? Math.min(1, phase * 1.4) : 0;
    const boulderY = 282 - height * 174 + fall * height * 174;
    return (
      <>
        <rect x="86" y="276" width="236" height="70" fill="#854d0e" />
        <rect x="86" y="124" width="96" height="222" fill="#78350f" />
        <circle cx="226" cy={boulderY} r="28" fill="#78716c" />
        <EnergyBar x={446} y={126} label="Stored Energy" value={dropping ? Math.max(0, height * (1 - fall)) : height} color="#38bdf8" />
        <EnergyBar x={446} y={176} label="Speed" value={dropping ? fall : 0} color="#ef4444" />
      </>
    );
  }

  if (title === 'The Great Transformation') {
    const angle = Math.sin(time / 18) * 0.9;
    const bobX = 360 + Math.sin(angle) * 170;
    const bobY = 112 + Math.cos(angle) * 170;
    const kinetic = Math.max(0, 1 - Math.abs(Math.sin(time / 18)));
    return (
      <>
        <line x1="360" y1="112" x2={bobX} y2={bobY} stroke="#e0f2fe" strokeWidth="5" />
        <circle cx={bobX} cy={bobY} r="30" fill="#facc15" />
        <EnergyBar x={82} y={104} label="Potential" value={1 - kinetic} color="#38bdf8" />
        <EnergyBar x={82} y={152} label="Kinetic" value={kinetic} color="#ef4444" />
      </>
    );
  }

  if (title === 'The Rule That Cannot Be Broken') {
    const bounce = Math.abs(Math.sin(time / 15));
    const heat = Math.min(0.86, (time % 180) / 180);
    const height = (1 - heat) * 170;
    const ballY = 318 - bounce * height;
    return (
      <>
        <Ground y={332} />
        <circle cx="244" cy={ballY} r="24" fill="#f97316" />
        <Pie x={520} y={196} heat={heat} />
        <text x="520" y="290" fill="#e0f2fe" fontSize="14" fontWeight="900" textAnchor="middle">energy total stays full</text>
      </>
    );
  }

  if (title === 'The Giant Machine') {
    const t = phase;
    const x = 82 + t * 560;
    const y = 298 - (Math.sin(t * Math.PI * 2.4) * 74 + Math.sin(t * Math.PI * 4.1) * 28 + 84);
    const potential = Math.max(0.08, (300 - y) / 190);
    return (
      <>
        <path d="M78 294 C170 88, 265 316, 380 178 S562 272, 650 128" fill="none" stroke="#94a3b8" strokeWidth="11" strokeLinecap="round" />
        <rect x={x - 28} y={y - 14} width="56" height="28" rx="8" fill="#facc15" />
        <EnergyBar x={80} y={92} label="Potential" value={potential} color="#38bdf8" />
        <EnergyBar x={80} y={140} label="Kinetic" value={1 - potential} color="#ef4444" />
      </>
    );
  }

  if (title === 'The Microscopic Dance') {
    const hot = sliderValue > 0.55;
    return (
      <>
        <rect x="152" y="100" width="416" height="194" rx="18" fill="rgba(14,165,233,0.12)" stroke="#7dd3fc" strokeWidth="3" />
        <Molecules hot={hot} time={time} count={36} x={152} y={100} width={416} height={194} />
        <text x="360" y="346" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">{hot ? 'hot: faster, larger jiggling' : 'cold: slow tiny vibration'}</text>
      </>
    );
  }

  if (title === 'One-Way Traffic') {
    const melt = phase;
    return (
      <>
        <rect x="160" y="152" width="150" height="112" rx="42" fill="#fb7185" opacity={0.95 - melt * 0.35} />
        <rect x="410" y={158 + melt * 36} width={106} height={106 - melt * 58} rx="12" fill="#60a5fa" />
        <ellipse cx="462" cy="302" rx={20 + melt * 58} ry={8 + melt * 10} fill="#38bdf8" opacity={melt} />
        {Array.from({ length: 5 }).map((_, index) => <Arrow key={index} x1={292 + index * 18} y1={186 + index * 16} x2={400 + index * 12} y2={186 + index * 16} color="#f97316" width={4} label={index === 0 ? 'heat flows hot → cold' : undefined} />)}
      </>
    );
  }

  if (title === 'The Three Paths of Heat') {
    return (
      <>
        <path d="M270 302 C292 264, 318 264, 340 302 C366 260, 394 260, 420 302" fill="#f97316" opacity="0.9" />
        <rect x="290" y="168" width="140" height="72" rx="18" fill="#334155" stroke="#e0f2fe" strokeWidth="5" />
        <line x1="430" y1="188" x2="590" y2="144" stroke="#94a3b8" strokeWidth="9" strokeLinecap="round" />
        <rect x="308" y="148" width="104" height="34" rx="16" fill="#60a5fa" />
        {selected === 0 && <Arrow x1="430" y1="188" x2="586" y2="145" color="#facc15" label="conduction through handle" width={5} />}
        {selected === 1 && (
          <>
            <path d="M330 160 C382 122, 414 188, 358 204 C306 218, 296 172, 330 160" fill="none" stroke="#38bdf8" strokeWidth="5" markerEnd="" />
            <Arrow x1="326" y1="198" x2="388" y2="166" color="#38bdf8" label="convection loop" width={4} />
          </>
        )}
        {selected === 2 && Array.from({ length: 4 }).map((_, index) => <path key={index} d={`M${300 + index * 30} 260 C${230 - index * 20} ${210 - index * 12}, ${180 - index * 24} ${184 + index * 14}, ${128} ${172 + index * 28}`} fill="none" stroke="#fb7185" strokeWidth="5" />)}
        {selected === 2 && <text x="128" y="132" fill="#fecaca" fontSize="14" fontWeight="900" textAnchor="middle">radiation to hands</text>}
      </>
    );
  }

  if (title === 'The Floating Fluid') {
    const hot = selected === 1;
    const rise = hot ? Math.min(1, phase * 1.5) : 0;
    return (
      <>
        <rect x="190" y="94" width="340" height="238" rx="18" fill="rgba(14,165,233,0.12)" stroke="#7dd3fc" strokeWidth="3" />
        <Molecules hot={false} time={time * 0.3} count={24} x={205} y={112 + rise * 60} width={310} height={190} />
        <g opacity={hot ? 1 : 0.4}>
          <ellipse cx="360" cy={276 - rise * 132} rx={80 + rise * 42} ry={36 + rise * 20} fill="rgba(251,113,133,0.22)" />
          <Molecules hot={hot} time={time} count={14} x={286} y={246 - rise * 132} width={150} height={72} />
        </g>
        {hot && <Arrow x1="360" y1="286" x2="360" y2="146" color="#f97316" label="hot less-dense air rises" width={5} />}
      </>
    );
  }

  if (title === 'The Temperature Trick') {
    return (
      <>
        <rect x="112" y="196" width="142" height="86" rx="10" fill="#92400e" />
        <rect x="466" y="196" width="142" height="86" rx="10" fill="#94a3b8" />
        <rect x="292" y="132" width="136" height="108" rx="46" fill="#fb7185" />
        <text x="183" y="310" fill="#fde68a" fontSize="16" fontWeight="900" textAnchor="middle">wood: slow heat loss</text>
        <text x="537" y="310" fill="#e0f2fe" fontSize="16" fontWeight="900" textAnchor="middle">metal: fast heat loss</text>
        <Arrow x1="292" y1="220" x2="228" y2="220" color="#f97316" label="small" width={3} />
        <Arrow x1="428" y1="220" x2="502" y2="220" color="#f97316" label="large" width={8} />
      </>
    );
  }

  if (title === 'The Thermos Magic') {
    return (
      <>
        <rect x="264" y="88" width="192" height="254" rx="48" fill="#111827" stroke="#e0f2fe" strokeWidth="5" />
        <rect x="306" y="126" width="108" height="182" rx="34" fill="#7f1d1d" />
        <path d="M288 112 L288 326 M432 112 L432 326" stroke="#020617" strokeWidth="18" />
        <text x="360" y="114" fill="#e0f2fe" fontSize="14" fontWeight="900" textAnchor="middle">vacuum gap</text>
        {Array.from({ length: 4 }).map((_, index) => (
          <path key={index} d={`M${332 + index * 18} ${172 + index * 26} C${282} ${178 + index * 22}, ${282} ${178 + index * 22}, ${314} ${178 + index * 22}`} fill="none" stroke="#f97316" strokeWidth="5" />
        ))}
        <text x="360" y="366" fill="#facc15" fontSize="16" fontWeight="900" textAnchor="middle">heat arrows stop at empty space</text>
      </>
    );
  }

  if (title === 'The Invisible Dominoes') {
    const radiusBase = (time * 3) % 240;
    return (
      <>
        <g transform={`translate(152 204) rotate(${Math.sin(time / 4) * 5})`}>
          <rect x="-12" y="-66" width="24" height="132" rx="12" fill="#e0f2fe" />
          <path d="M-42 -58 L-12 -58 M12 -58 L42 -58" stroke="#e0f2fe" strokeWidth="12" strokeLinecap="round" />
        </g>
        {Array.from({ length: 4 }).map((_, ring) => {
          const r = (radiusBase + ring * 62) % 260;
          return <circle key={ring} cx="152" cy="204" r={r} fill="none" stroke="#94a3b8" strokeWidth="6" strokeDasharray="6 14" opacity={1 - r / 280} />;
        })}
        <path d="M590 152 C640 176, 640 234, 590 258 C608 222, 608 188, 590 152" fill="#facc15" />
        <text x="590" y="300" fill="#e0f2fe" fontSize="14" fontWeight="900" textAnchor="middle">ear</text>
      </>
    );
  }

  if (title === 'The Anatomy of a Wave') {
    const amplitude = 18 + sliderValue * 72;
    const frequency = 0.1 + sliderBValue * 0.34;
    return (
      <>
        <path
          d={Array.from({ length: 92 }).map((_, index) => {
            const x = 70 + index * 6.4;
            const y = 210 + Math.sin(index * frequency + time / 8) * amplitude;
            return `${index === 0 ? 'M' : 'L'}${x},${y}`;
          }).join(' ')}
          fill="none"
          stroke="#38bdf8"
          strokeWidth="6"
        />
        <text x="190" y="112" fill="#e0f2fe" fontSize="17" fontWeight="900" textAnchor="middle">slider changes amplitude/frequency</text>
      </>
    );
  }

  if (title === 'The Straight and Narrow') {
    const angle = -0.55 + sliderValue * 0.9;
    const hitX = 360;
    const hitY = 210 + Math.sin(angle) * 100;
    return (
      <>
        <line x1="360" y1="92" x2="360" y2="326" stroke="#e0f2fe" strokeWidth="8" />
        <rect x="118" y="258" width="94" height="32" rx="10" fill="#475569" transform={`rotate(${angle * 57.3} 165 274)`} />
        <line x1="180" y1="258" x2={hitX} y2={hitY} stroke="#ef4444" strokeWidth="6" />
        <line x1={hitX} y1={hitY} x2={560} y2={258} stroke="#ef4444" strokeWidth="6" />
        <text x="360" y="366" fill="#facc15" fontSize="16" fontWeight="900" textAnchor="middle">angle in = angle out</text>
      </>
    );
  }

  if (title === 'The Speed of Light Changes') {
    return (
      <>
        <rect x="68" y="206" width="584" height="122" fill="rgba(56,189,248,0.28)" />
        <line x1="92" y1="108" x2="328" y2="206" stroke="#ef4444" strokeWidth="6" />
        <line x1="328" y1="206" x2="442" y2="318" stroke="#ef4444" strokeWidth="6" />
        <text x="166" y="146" fill="#e0f2fe" fontSize="18" fontWeight="900">Air: 1.0c</text>
        <text x="456" y="276" fill="#bae6fd" fontSize="18" fontWeight="900">Water: 0.75c</text>
        <text x="360" y="366" fill="#facc15" fontSize="16" fontWeight="900" textAnchor="middle">beam bends as it slows in water</text>
      </>
    );
  }

  if (title === "Nature's Prism") {
    return (
      <>
        <circle cx="360" cy="202" r="104" fill="rgba(125,211,252,0.2)" stroke="#bae6fd" strokeWidth="6" />
        <line x1="112" y1="112" x2="286" y2="164" stroke="#ffffff" strokeWidth="7" />
        <line x1="286" y1="164" x2="430" y2="174" stroke="#ffffff" strokeWidth="6" />
        <line x1="430" y1="174" x2="314" y2="254" stroke="#ffffff" strokeWidth="6" />
        {['#ef4444', '#f97316', '#facc15', '#22c55e', '#38bdf8', '#6366f1', '#a855f7'].map((color, index) => (
          <line key={color} x1="314" y1="254" x2={134 + index * 18} y2={318 + index * 8} stroke={color} strokeWidth="5" />
        ))}
        <text x="360" y="354" fill="#e0f2fe" fontSize="16" fontWeight="900" textAnchor="middle">refraction + reflection spreads white light</text>
      </>
    );
  }

  if (title === 'The Stubbornness of Stuff') {
    const pushed = selected === 1;
    const rockX = pushed ? 112 + phase * 520 : 156;
    return (
      <>
        <Ground y={326} rough={!pushed} />
        {pushed && <rect x="238" y="326" width="420" height="18" rx="9" fill="#bae6fd" />}
        <ellipse cx={rockX} cy="292" rx="36" ry="26" fill="#78716c" />
        {!pushed && <text x="360" y="116" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">resting rock: timer runs, no motion</text>}
        {pushed && <Arrow x1={rockX - 78} y1="238" x2={rockX + 118} y2="238" color="#38bdf8" label="frictionless glide keeps going" width={6} />}
      </>
    );
  }

  if (title === 'The Seatbelt Savior') {
    const belted = selected === 1;
    const p = Math.min(1, phase * 1.3);
    const carX = 128 + p * 330;
    const dummyX = belted ? carX + 8 : carX + Math.max(0, p - 0.56) * 250;
    return (
      <>
        <rect x="548" y="116" width="34" height="218" rx="6" fill="#94a3b8" />
        <rect x={carX - 82} y="204" width="164" height="88" rx="18" fill="#38bdf8" />
        <circle cx={dummyX} cy="246" r="22" fill="#facc15" />
        {belted && <path d={`M${carX - 34} 220 L${dummyX + 18} 270`} stroke="#22c55e" strokeWidth="9" strokeLinecap="round" />}
        {belted ? <Arrow x1={dummyX + 42} y1="170" x2={dummyX - 24} y2="170" color="#22c55e" label="seatbelt force catches body" width={5} /> : <Arrow x1={dummyX - 18} y1="170" x2={dummyX + 150} y2="170" color="#fb7185" label="body keeps moving" width={6} />}
      </>
    );
  }

  if (title === 'The Concept of Equilibrium') {
    const shipX = 430 + ((time * 1.2) % 180);
    return (
      <>
        <rect x="58" y="96" width="284" height="226" rx="16" fill="#082f49" />
        <rect x="378" y="96" width="284" height="226" rx="16" fill="#020617" />
        <Ground y={284} />
        <ellipse cx="200" cy="254" rx="36" ry="25" fill="#78716c" />
        <g transform={`translate(${shipX} 214)`}>
          <polygon points="-50,0 26,-20 60,0 26,20" fill="#e0f2fe" />
          <circle cx="-16" cy="0" r="8" fill="#38bdf8" />
        </g>
        <text x="200" y="132" fill="#22c55e" fontSize="18" fontWeight="900" textAnchor="middle">0 N Net Force</text>
        <text x="520" y="132" fill="#22c55e" fontSize="18" fontWeight="900" textAnchor="middle">0 N Net Force</text>
        <text x="360" y="362" fill="#22c55e" fontSize="20" fontWeight="900" textAnchor="middle">EQUILIBRIUM</text>
      </>
    );
  }

  if (title === 'The Unbalanced Force') {
    const unbalanced = selected === 1;
    const ropeX = unbalanced ? 330 + phase * 170 : 330;
    return (
      <>
        <Ground y={322} />
        <line x1="116" y1="230" x2={ropeX} y2="230" stroke="#e0f2fe" strokeWidth="7" />
        <line x1={ropeX + 82} y1="230" x2="622" y2="230" stroke="#e0f2fe" strokeWidth="7" />
        <Box x={ropeX} y="194" color="#7dd3fc" label="box" />
        <Arrow x1="280" y1="150" x2="174" y2="150" color="#fb7185" label={unbalanced ? '40 N left' : '30 N left'} width={5} />
        <Arrow x1="430" y1="150" x2={unbalanced ? 592 : 536} y2="150" color="#22c55e" label={unbalanced ? 'extra right force' : '30 N right'} width={unbalanced ? 8 : 5} />
        <text x="360" y="96" fill={unbalanced ? '#fb7185' : '#22c55e'} fontSize="20" fontWeight="900" textAnchor="middle">{unbalanced ? 'UNBALANCED FORCE: accelerates right' : 'Equilibrium: rope still'}</text>
      </>
    );
  }

  if (title === 'Mass and Inertia') {
    const pushed = selected === 1;
    const emptyX = 112 + (pushed ? phase * 430 : 0);
    const fullX = 112 + (pushed ? phase * 74 : 0);
    return (
      <>
        <Ground y={182} />
        <Ground y={326} />
        <rect x={emptyX} y="124" width="118" height="42" rx="10" fill="#38bdf8" />
        <circle cx={emptyX + 24} cy="178" r="12" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <circle cx={emptyX + 92} cy="178" r="12" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <rect x={fullX} y="248" width="118" height="42" rx="10" fill="#f97316" />
        {Array.from({ length: 4 }).map((_, index) => <circle key={index} cx={fullX + 22 + index * 24} cy="232" r="15" fill="#78716c" />)}
        <circle cx={fullX + 24} cy="302" r="12" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <circle cx={fullX + 92} cy="302" r="12" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <Arrow x1="84" y1="96" x2="184" y2="96" color="#22c55e" label="same push: empty cart zooms" width={5} />
        <Arrow x1="84" y1="218" x2="184" y2="218" color="#22c55e" label="same push: full cart inches" width={5} />
      </>
    );
  }

  if (title === 'Stopping Mass') {
    const p = phase;
    const bikeX = Math.min(310, 90 + p * 420);
    const trainX = 80 + p * 530;
    return (
      <>
        <Ground y={174} />
        <Ground y={326} />
        <rect x="346" y="102" width="32" height="72" fill="#b45309" />
        <rect x="346" y="254" width="32" height="72" fill={trainX > 350 ? '#78350f' : '#b45309'} transform={trainX > 350 ? 'rotate(16 362 290)' : undefined} />
        <Bicycle x={bikeX} y={106} time={time} />
        <rect x={trainX - 90} y="236" width="180" height="70" rx="10" fill="#475569" />
        <circle cx={trainX - 50} cy="314" r="15" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <circle cx={trainX + 50} cy="314" r="15" fill="#020617" stroke="#e0f2fe" strokeWidth="4" />
        <text x="360" y="72" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">same speed, very different mass</text>
      </>
    );
  }

  if (title === 'The Classic Magic Trick') {
    const flick = Math.min(1, phase * 1.35);
    const cardX = 280 + flick * 420;
    const coinY = 118 + Math.max(0, flick - 0.18) * 230;
    return (
      <>
        <path d="M286 246 L434 246 L408 342 L312 342 Z" fill="none" stroke="#bae6fd" strokeWidth="6" />
        <rect x={cardX} y="208" width="160" height="20" rx="5" fill="#f8fafc" />
        <circle cx="360" cy={coinY} r="25" fill="#facc15" />
        <line x1="142" y1="214" x2={cardX} y2="214" stroke="#fca5a5" strokeWidth="14" strokeLinecap="round" />
        <Arrow x1="190" y1="170" x2="380" y2="170" color="#fb7185" label="fast horizontal flick" width={6} />
        <Arrow x1="360" y1="144" x2="360" y2="292" color="#38bdf8" label="coin drops straight down" width={4} />
      </>
    );
  }

  if (title === 'The Seatbelt Lock') {
    const crash = selected === 1;
    const swing = crash ? Math.min(1, phase * 1.7) : 0;
    return (
      <>
        <rect x="196" y="108" width="328" height="210" rx="20" fill="#111827" stroke="#94a3b8" strokeWidth="5" />
        <circle cx="430" cy="210" r="58" fill="none" stroke="#38bdf8" strokeWidth="12" />
        <path d="M430 152 L430 268 M372 210 L488 210" stroke="#38bdf8" strokeWidth="6" />
        <line x1="292" y1="142" x2={292 + swing * 72} y2={254 - swing * 50} stroke="#e0f2fe" strokeWidth="7" strokeLinecap="round" />
        <circle cx={292 + swing * 72} cy={254 - swing * 50} r="24" fill="#facc15" />
        {crash && <polygon points="380,190 430,210 380,230" fill="#ef4444" />}
        <text x="360" y="358" fill={crash ? '#facc15' : '#e0f2fe'} fontSize="18" fontWeight="900" textAnchor="middle">{crash ? 'pendulum inertia jams tooth into gear: belt locks' : 'smooth driving: pendulum hangs down'}</text>
      </>
    );
  }

  if (title === 'The Master Equation') {
    const force = Math.round(sliderValue * 100);
    const accel = force / 10;
    return (
      <>
        <Ground y={314} />
        <Box x="300" y="242" color="#7dd3fc" label="10kg" />
        <Arrow x1="180" y1="210" x2={180 + 90 + sliderValue * 260} y2="210" color="#22c55e" label={`F = ${force} N`} width={4 + sliderValue * 7} />
        <text x="360" y="112" fill="#facc15" fontSize="28" fontWeight="900" textAnchor="middle">F = ma</text>
        <text x="360" y="152" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">a = {accel.toFixed(1)} m/s²</text>
      </>
    );
  }

  if (title === 'The Math of the Push') {
    const force = Math.round(sliderValue * 20);
    const correct = force === 10 || selected === 1;
    return (
      <>
        <Ground y={314} />
        <Box x={correct ? 430 + phase * 120 : 300} y="242" color="#7dd3fc" label="5kg" />
        <Arrow x1="160" y1="210" x2={160 + force * 13} y2="210" color={correct ? '#22c55e' : '#f97316'} label={`${force} N`} width={5} />
        <text x="360" y="104" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">target: 2 m/s²</text>
        <text x="360" y="144" fill={correct ? '#22c55e' : '#facc15'} fontSize="22" fontWeight="900" textAnchor="middle">5 kg × 2 m/s² = 10 N</text>
      </>
    );
  }

  if (title === 'The Mass Penalty') {
    const bikeX = 122 + phase * 430;
    const truckX = 118 + phase * 62;
    return (
      <>
        <Ground y={182} />
        <Ground y={326} />
        <Bicycle x={bikeX} y={106} time={time} />
        <Car x={truckX} y={278} scale={1.35} color="#64748b" />
        <Arrow x1="88" y1="82" x2="210" y2="82" color="#22c55e" label="100 N push: high acceleration" width={5} />
        <Arrow x1="88" y1="220" x2="210" y2="220" color="#22c55e" label="100 N push: near zero acceleration" width={5} />
      </>
    );
  }

  if (title === 'The Math of the Heavy') {
    const mass = 5 + Math.round(sliderValue * 15);
    const accel = 20 / mass;
    return (
      <>
        <Ground y={314} />
        <Box x="300" y="242" color="#7dd3fc" label={`${mass}kg`} />
        <Arrow x1="170" y1="210" x2="294" y2="210" color="#22c55e" label="20 N" width={5} />
        <text x="360" y="112" fill="#facc15" fontSize="26" fontWeight="900" textAnchor="middle">a = F / m</text>
        <text x="360" y="156" fill="#e0f2fe" fontSize="22" fontWeight="900" textAnchor="middle">20 N / {mass} kg = {accel.toFixed(1)} m/s²</text>
        <EnergyBar x={466} y={248} label="acceleration" value={accel / 4} color="#38bdf8" />
      </>
    );
  }

  if (title === 'The Universal Constant') {
    const place = selected === 1 ? 'Deep Space' : selected === 2 ? 'Moon' : 'Earth';
    return (
      <>
        <circle cx="360" cy="214" r={selected === 1 ? 22 : 82} fill={selected === 2 ? '#cbd5e1' : selected === 1 ? '#facc15' : '#38bdf8'} opacity="0.7" />
        <Person x={360} y={142} />
        <text x="360" y="94" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">{place}</text>
        <text x="360" y="336" fill="#facc15" fontSize="24" fontWeight="900" textAnchor="middle">Mass: 60 kg • Total atoms: unchanged</text>
      </>
    );
  }

  if (title === 'Weight is a Force') {
    const moon = selected === 1;
    return (
      <>
        <Ground y={322} />
        <rect x="278" y="284" width="164" height="34" rx="10" fill="#475569" />
        <Box x="318" y="208" color="#7dd3fc" label="10kg" />
        <Arrow x1="360" y1="158" x2="360" y2={moon ? 222 : 284} color="#facc15" label={moon ? 'W = 10 × 1.6 = 16 N' : 'W = 10 × 9.8 = 98 N'} width={moon ? 4 : 8} />
        <text x="360" y="112" fill="#e0f2fe" fontSize="20" fontWeight="900" textAnchor="middle">{moon ? 'Moon gravity scale' : 'Earth gravity scale'}</text>
      </>
    );
  }

  if (title === 'Adding Up The Pushes') {
    const calculated = selected === 1;
    return (
      <>
        <Ground y={314} />
        <Box x="318" y="242" color="#7dd3fc" label="box" />
        {!calculated ? (
          <>
            <Arrow x1="230" y1="176" x2="340" y2="176" color="#38bdf8" label="10 N" width={4} />
            <Arrow x1="230" y1="214" x2="390" y2="214" color="#38bdf8" label="15 N" width={5} />
            <Arrow x1="500" y1="250" x2="330" y2="250" color="#fb7185" label="20 N" width={5} />
          </>
        ) : (
          <Arrow x1="292" y1="198" x2="430" y2="198" color="#22c55e" label="Net Force = 5 N right" width={7} />
        )}
      </>
    );
  }

  if (title === 'The Math of the Tug') {
    return (
      <>
        <Ground y={314} />
        <Box x="318" y="242" color="#7dd3fc" label="cart" />
        <Arrow x1="510" y1="180" x2="330" y2="180" color="#fb7185" label="100 N Left" width={8} />
        <Arrow x1="220" y1="228" x2="342" y2="228" color="#38bdf8" label="60 N Right" width={5} />
        <text x="360" y="110" fill="#facc15" fontSize="24" fontWeight="900" textAnchor="middle">100 - 60 = 40 N Left</text>
      </>
    );
  }

  if (title === 'The Law of Action and Reaction') {
    const p = Math.min(1, phase * 1.3);
    return (
      <>
        <Ground y={314} />
        <rect x="470" y="166" width="46" height="148" rx="5" fill="#b45309" />
        <g transform={`translate(${270 - p * 180} 0)`}><Person x={0} y={154} /></g>
        <rect x={234 - p * 180} y="286" width="86" height="18" rx="9" fill="#38bdf8" />
        <Arrow x1="322" y1="196" x2="462" y2="196" color="#fb7185" label="Action Push" width={6} />
        <Arrow x1="462" y1="232" x2={300 - p * 60} y2="232" color="#38bdf8" label="Reaction Push" width={6} />
      </>
    );
  }

  if (title === 'The Rocket in the Vacuum') {
    const ignited = selected === 1;
    const rocketY = ignited ? 254 - phase * 170 : 254;
    return (
      <>
        <rect x="54" y="78" width="612" height="266" rx="18" fill="#020617" />
        <g transform={`translate(360 ${rocketY})`}>
          <polygon points="0,-78 -36,10 36,10" fill="#e0f2fe" />
          <rect x="-28" y="6" width="56" height="78" rx="14" fill="#94a3b8" />
          {ignited && <path d="M-28 84 C-58 156, 58 156, 28 84" fill="#fb7185" opacity="0.9" />}
        </g>
        {ignited && <Arrow x1="360" y1={rocketY + 130} x2="360" y2={rocketY + 224} color="#fb7185" label="exhaust action" width={7} />}
        {ignited && <Arrow x1="440" y1={rocketY + 24} x2="440" y2={rocketY - 84} color="#22c55e" label="rocket reaction" width={7} />}
      </>
    );
  }

  if (title === 'Mass in Motion') {
    const brakes = selected === 1;
    const ballX = brakes ? Math.min(250, 112 + phase * 210) : 112 + phase * 460;
    const boulderX = 112 + phase * (brakes ? 430 : 460);
    return (
      <>
        <Ground y={184} />
        <Ground y={326} />
        <Ball x={ballX} y={152} color="#38bdf8" />
        <circle cx={boulderX} cy="282" r="48" fill="#57534e" />
        {brakes && <rect x="286" y="116" width="22" height="70" fill="#22c55e" />}
        {brakes && <rect x="286" y="236" width="22" height="90" fill="#ef4444" transform={boulderX > 300 ? 'rotate(22 297 280)' : undefined} />}
        <text x="360" y="82" fill="#e0f2fe" fontSize="18" fontWeight="900" textAnchor="middle">same speed, huge mass means huge momentum</text>
      </>
    );
  }

  if (title === 'The Formula for Destruction') {
    const mass = 1 + sliderValue * 9;
    const velocity = 1 + sliderBValue * 9;
    const momentum = mass * velocity;
    return (
      <>
        <Ground y={314} />
        <Car x={120 + phase * 470} y={282} scale={0.8 + mass / 16} color="#64748b" />
        <EnergyBar x={430} y={132} label="Momentum p = mv" value={momentum / 100} color="#f97316" />
        <text x="360" y="94" fill="#facc15" fontSize="24" fontWeight="900" textAnchor="middle">p = m × v = {momentum.toFixed(0)}</text>
      </>
    );
  }

  if (title === 'The Problem of Stopping') {
    const p = Math.min(1, phase * 1.3);
    const carX = Math.min(452, 110 + p * 400);
    return (
      <>
        <Ground y={314} />
        <rect x="526" y="160" width="36" height="154" rx="6" fill="#b45309" />
        <Car x={carX} y={282} color="#38bdf8" />
        <EnergyBar x={92} y={112} label="Stopping Time: 0.1 s" value={0.1} color="#38bdf8" />
        <EnergyBar x={92} y={164} label="Force Meter: lethal spike" value={p > 0.76 ? 1 : 0.3} color="#ef4444" />
      </>
    );
  }

  if (title === 'Stretching the Clock') {
    const airbag = selected === 1;
    const p = Math.min(1, phase * 1.2);
    const carX = Math.min(452, 110 + p * 400);
    return (
      <>
        <Ground y={314} />
        <rect x="526" y="160" width="36" height="154" rx="6" fill="#b45309" />
        <Car x={carX} y={282} color="#38bdf8" />
        {airbag && <ellipse cx="486" cy="238" rx={20 + p * 46} ry={18 + p * 34} fill="#e0f2fe" opacity="0.9" />}
        <EnergyBar x={92} y={112} label={airbag ? 'Stopping Time: 2.0 s' : 'Stopping Time: 0.1 s'} value={airbag ? 0.9 : 0.1} color="#38bdf8" />
        <EnergyBar x={92} y={164} label={airbag ? 'Force Meter: safe' : 'Force Meter: dangerous'} value={airbag ? 0.28 : 0.95} color={airbag ? '#22c55e' : '#ef4444'} />
      </>
    );
  }

  if (title === 'The Law of Conservation of Momentum') {
    const p = Math.min(1, phase * 1.35);
    const before = p < 0.5;
    const blueX = before ? 120 + p * 430 : 335;
    const redX = before ? 426 : 426 + (p - 0.5) * 460;
    return (
      <>
        <rect x="70" y="314" width="580" height="14" rx="7" fill="#bae6fd" />
        <rect x={blueX} y="252" width="86" height="52" rx="9" fill="#38bdf8" />
        <rect x={redX} y="252" width="86" height="52" rx="9" fill="#fb7185" />
        <line x1="104" y1="112" x2="616" y2="112" stroke="#22c55e" strokeWidth="6" />
        <text x="360" y="92" fill="#22c55e" fontSize="18" fontWeight="900" textAnchor="middle">Total Momentum graph stays flat</text>
      </>
    );
  }

  if (title === 'Inelastic Collisions') {
    const p = Math.min(1, phase * 1.2);
    const hit = p > 0.5;
    const car1X = hit ? 310 + (p - 0.5) * 160 : 90 + p * 440;
    const car2X = hit ? car1X + 110 : 430;
    return (
      <>
        <Ground y={314} />
        <rect x={car1X} y="238" width="104" height="58" rx="8" fill="#38bdf8" />
        <rect x={car2X} y="238" width="104" height="58" rx="8" fill="#f97316" />
        {hit && <rect x={car1X + 96} y="258" width="22" height="18" rx="5" fill="#facc15" />}
        <text x="360" y="112" fill="#facc15" fontSize="22" fontWeight="900" textAnchor="middle">{hit ? 'locked together: 5 m/s' : 'incoming car: 10 m/s'}</text>
      </>
    );
  }

  return null;
};

const GeneralScene = ({ spec, selected, slider, time }: { spec: SimulationSpec; selected: number; slider: number; time: number }) => {
  const t = (time % 120) / 120;
  const value = slider / 100;
  const alt = selected === 1;
  const x = 120 + ((time * (1.2 + value * 2.4)) % 480);
  const arrowSize = 72 + value * 118;

  switch (spec.sceneKind) {
    case 'gravity':
      return (
        <>
          <Ground y={330} />
          <circle cx={x} cy={110 + t * 190} r="22" fill="#ef4444" />
          <Arrow x1={x} y1={160 + t * 160} x2={x} y2={232 + t * 86} color="#60a5fa" label="gravity" width={5} />
        </>
      );
    case 'friction':
      return (
        <>
          <Ground y={302} rough={alt || /rough/i.test(spec.text)} />
          <Ball x={alt ? 112 + Math.min(t * 1.3, 1) * 342 : x} y={270} color="#93c5fd" />
          <Arrow x1={x - 50} y1={214} x2={x + (alt ? 40 + (1 - Math.min(t, 1)) * 70 : 120)} y2={214} color="#22c55e" label="motion" />
          <Arrow x1={x + 44} y1={326} x2={x - 54} y2={326} color="#fb7185" label="friction" width={alt ? 6 : 3} />
        </>
      );
    case 'motion':
      return (
        <>
          <Ground y={312} />
          <Ball x={x} y={280} color="#60a5fa" />
          <Arrow x1={x - 72} y1={222} x2={x + arrowSize} y2={alt ? 176 : 222} color="#22c55e" label={alt ? 'velocity direction' : 'speed'} />
        </>
      );
    case 'machine':
      return (
        <>
          <Ground y={324} />
          <line x1="158" y1={alt ? 254 : 284} x2="560" y2={alt ? 190 : 284} stroke="#94a3b8" strokeWidth="12" strokeLinecap="round" />
          <polygon points="340,310 380,310 360,245" fill="#facc15" />
          <Box x={alt ? 470 : 190} y={alt ? 148 : 228} color="#7dd3fc" label="load" />
          <Arrow x1="220" y1="196" x2={alt ? 318 : 262} y2={alt ? 178 : 196} color="#22c55e" label="small force" width={4} />
        </>
      );
    case 'work':
    case 'energy':
      return (
        <>
          <path d="M78 302 C190 136, 294 350, 420 168 S570 282, 650 142" fill="none" stroke="#94a3b8" strokeWidth="10" />
          <Ball x={100 + t * 520} y={alt ? 210 + Math.sin(t * Math.PI * 3) * 58 : 246 - Math.sin(t * Math.PI) * 112} color="#fbbf24" />
          <Arrow x1="92" y1="86" x2={92 + arrowSize} y2="86" color="#22c55e" label={spec.sceneKind === 'work' ? 'force over distance' : 'energy changes form'} />
          <rect x="82" y="112" width="190" height="16" rx="8" fill="#1e293b" />
          <rect x="82" y="112" width={40 + value * 150} height="16" rx="8" fill="#38bdf8" />
        </>
      );
    case 'thermal':
    case 'thermo':
    case 'gas':
      return (
        <>
          <rect x="150" y="98" width={alt ? 370 : 280} height="188" rx="18" fill="rgba(14,165,233,0.12)" stroke="#7dd3fc" strokeWidth="3" />
          {Array.from({ length: 34 }).map((_, index) => {
            const speed = 0.35 + value * 1.7 + (alt ? 0.5 : 0);
            const px = 174 + ((index * 41 + time * speed * (index % 3 + 1)) % (alt ? 314 : 224));
            const py = 124 + ((index * 23 + time * speed * 1.4) % 132);
            return <circle key={index} cx={px} cy={py} r="5" fill={index % 2 ? '#fca5a5' : '#93c5fd'} />;
          })}
          <Arrow x1="176" y1="336" x2={176 + arrowSize} y2="336" color="#f97316" label={spec.sceneKind === 'gas' ? 'particle hits' : 'thermal motion'} />
        </>
      );
    case 'wave':
    case 'oscillator':
      return (
        <>
          <path
            d={Array.from({ length: 90 }).map((_, index) => {
              const px = 72 + index * 6.4;
              const py = 210 + Math.sin(index * (alt ? 0.34 : 0.2) + time / 7) * (32 + value * 48);
              return `${index === 0 ? 'M' : 'L'}${px},${py}`;
            }).join(' ')}
            fill="none"
            stroke="#38bdf8"
            strokeWidth="6"
          />
          <Arrow x1="110" y1="310" x2="250" y2="310" color="#22c55e" label="energy travels" width={4} />
          {spec.sceneKind === 'oscillator' && <rect x={330 + Math.sin(time / 8) * (40 + value * 70)} y="252" width="70" height="56" rx="10" fill="#fbbf24" />}
        </>
      );
    case 'momentum':
      return (
        <>
          <Ground y={312} />
          <rect x={120 + t * 240} y="242" width="106" height="56" rx="10" fill="#38bdf8" />
          <rect x={alt ? 418 - t * 110 : 470} y="242" width="120" height="56" rx="10" fill="#fb7185" />
          <Arrow x1={116 + t * 240} y1="206" x2={116 + t * 240 + arrowSize} y2="206" color="#22c55e" label="momentum" />
          {alt && <Arrow x1={540 - t * 100} y1="206" x2={440 - t * 40} y2="206" color="#60a5fa" label="opposite" width={4} />}
        </>
      );
    case 'rotation':
      return (
        <>
          <g transform={`rotate(${time * (alt ? 2.8 : 1.2)},360,210)`}>
            <rect x="240" y="182" width="240" height="56" rx="28" fill="#38bdf8" />
            <circle cx="286" cy="210" r="22" fill="#0f172a" />
            <circle cx="434" cy="210" r="22" fill="#0f172a" />
          </g>
          <line x1="360" y1="94" x2="360" y2="328" stroke="#facc15" strokeWidth="4" strokeDasharray="8 8" />
          <Arrow x1="460" y1="126" x2="570" y2="104" color="#22c55e" label="torque" width={4} />
        </>
      );
    case 'fluid':
      return (
        <>
          <path d="M70 224 C170 164, 260 284, 378 220 S560 164, 650 222" fill="none" stroke="#38bdf8" strokeWidth={alt ? 18 : 10} strokeLinecap="round" />
          <path d="M70 270 C170 210, 260 330, 378 266 S560 210, 650 268" fill="none" stroke="#7dd3fc" strokeWidth="6" strokeLinecap="round" />
          <Arrow x1="126" y1="156" x2={126 + arrowSize} y2="156" color="#22c55e" label={alt ? 'faster flow' : 'pressure'} />
          <rect x="386" y="146" width={alt ? 120 : 74} height="128" rx="12" fill="rgba(14,165,233,0.18)" stroke="#e0f2fe" />
        </>
      );
    case 'field':
    case 'phase':
    case 'continuum':
    case 'chaos':
    default:
      return (
        <>
          <rect x="110" y="82" width="500" height="238" rx="16" fill="#020617" stroke="rgba(148,163,184,0.28)" />
          {Array.from({ length: spec.sceneKind === 'field' ? 18 : 1 }).map((_, index) => {
            const angle = (index / 18) * Math.PI * 2;
            return spec.sceneKind === 'field'
              ? <Arrow key={index} x1="360" y1="204" x2={360 + Math.cos(angle) * (72 + value * 74)} y2={204 + Math.sin(angle) * (72 + value * 74)} color="#22c55e" width={3} />
              : null;
          })}
          {spec.sceneKind !== 'field' && (
            <path
              d={Array.from({ length: 120 }).map((_, index) => {
                const a = index / 9;
                const radius = spec.sceneKind === 'chaos' ? 18 + index * (0.86 + value * 0.35) : 130 - index * 0.5;
                const px = 360 + Math.sin(a * (alt ? 1.7 : 1.1)) * radius;
                const py = 204 + Math.cos(a * (1.2 + value)) * radius * 0.55;
                return `${index === 0 ? 'M' : 'L'}${px},${py}`;
              }).join(' ')}
              fill="none"
              stroke="#38bdf8"
              strokeWidth="4"
            />
          )}
          <Arrow x1="128" y1="348" x2={128 + arrowSize} y2="348" color="#facc15" label={spec.sceneKind === 'chaos' ? 'small change grows' : compact(spec.labels[0], 'state')} width={4} />
        </>
      );
  }
};

const needsSecondSlider = (spec: SimulationSpec) =>
  ['The Anatomy of a Wave', 'The Formula for Destruction'].includes(spec.title) ||
  (spec.gradeLevel === 9 && ['The Formula: W = Fd', 'Doing the Math', 'The Equation for Height'].includes(spec.title)) ||
  (spec.gradeLevel === 10 && spec.title === 'The Equation: p = mv');

const needsCustomSlider = (spec: SimulationSpec) =>
  [
    'The Feeling of Acceleration',
    'The Distance Trade-Off',
    'Energy in Action and in Waiting',
    "Gravity's Battery",
    'The Microscopic Dance',
    'The Anatomy of a Wave',
    'The Straight and Narrow',
    'The Master Equation',
    'The Math of the Push',
    'The Math of the Heavy',
    'The Formula for Destruction',
  ].includes(spec.title) ||
  (spec.gradeLevel === 9 && [
    'The Formula: W = Fd',
    'Angles and Work',
    'Working Against Friction',
    'The Great Trade-Off',
    'Mechanical Advantage',
    'The Kinetic Energy Equation',
    'Doing the Math',
    'The Equation for Height',
    'Elastic Potential Energy',
    'The Power Formula',
    'The Friction Tax',
  ].includes(spec.title)) ||
  (spec.gradeLevel === 10 && [
    'The Equation: p = mv',
    'Symmetry vs Asymmetry',
    'Torque: The Rotational Push',
    'Rotational Equilibrium',
    'The Base of Support',
    'High vs Low Centers',
  ].includes(spec.title));

const primaryControlFor = (spec: SimulationSpec) => {
  if (spec.gradeLevel === 9) {
    if (spec.title === 'The Formula: W = Fd') return 'Force';
    if (spec.title === 'Angles and Work') return 'Rope angle';
    if (spec.title === 'Working Against Friction') return 'Push force';
    if (['The Great Trade-Off', 'Mechanical Advantage'].includes(spec.title)) return 'Ramp length';
    if (spec.title === 'The Kinetic Energy Equation') return 'Speed';
    if (spec.title === 'Doing the Math') return /KE =|Kinetic Energy/i.test(spec.text) ? 'Mass' : 'Place tiles';
    if (spec.title === 'The Equation for Height') return 'Mass';
    if (spec.title === 'Elastic Potential Energy') return 'Spring stretch';
    if (spec.title === 'The Power Formula') return 'Time';
    if (spec.title === 'The Friction Tax') return /engine|piston|Lubrication/i.test(spec.text) ? 'Lubrication' : 'Friction';
  }
  if (spec.gradeLevel === 10) {
    if (spec.title === 'The Equation: p = mv') return 'Mass';
    if (spec.title === 'Symmetry vs Asymmetry') return 'Fulcrum position';
    if (spec.title === 'Torque: The Rotational Push') return 'Push location';
    if (spec.title === 'Rotational Equilibrium') return 'Right weight distance';
    if (spec.title === 'The Base of Support') return 'Tilt';
    if (spec.title === 'High vs Low Centers') return 'Turn speed';
  }
  if (spec.title === 'The Feeling of Acceleration') return 'Brake / Accelerate';
  if (spec.title === 'The Distance Trade-Off') return 'Ramp steepness';
  if (spec.title === 'Energy in Action and in Waiting') return 'Pull back';
  if (spec.title === "Gravity's Battery") return 'Height';
  if (spec.title === 'The Microscopic Dance') return 'Temperature';
  if (spec.title === 'The Anatomy of a Wave') return 'Amplitude';
  if (spec.title === 'The Straight and Narrow') return 'Laser angle';
  if (spec.title === 'The Master Equation') return 'Pushing force';
  if (spec.title === 'The Math of the Push') return 'Force meter';
  if (spec.title === 'The Math of the Heavy') return 'Mass denominator';
  if (spec.title === 'The Formula for Destruction') return 'Mass';
  return spec.sliderLabel;
};

const secondaryControlFor = (spec: SimulationSpec) => {
  if (spec.title === 'The Anatomy of a Wave') return 'Frequency';
  if (spec.title === 'The Formula for Destruction') return 'Velocity';
  if (spec.gradeLevel === 9 && spec.title === 'The Formula: W = Fd') return 'Distance';
  if (spec.gradeLevel === 9 && spec.title === 'Doing the Math') return 'Velocity';
  if (spec.gradeLevel === 9 && spec.title === 'The Equation for Height') return 'Height';
  if (spec.gradeLevel === 10 && spec.title === 'The Equation: p = mv') return 'Velocity';
  return 'Secondary';
};

const calibrationButtonsFor = (spec: SimulationSpec, isTradingDistance: boolean, isCatapultRelease: boolean, isRuleOfLaziness: boolean) => {
  if (spec.gradeLevel === 9) {
    if (spec.title === 'Direction Trap') return ['Walk Forward'];
    if (spec.title === 'Working Against Friction') return ['Ice', 'Sandpaper'];
    if (spec.title === 'Elastic Potential Energy') return ['Release'];
    if (spec.title === 'The Law of Conservation of Energy') return ['Drop Ball', 'Fire Rocket', 'Slide Box'];
    if (spec.title === 'The Element of Time') return ['Walk', 'Sprint'];
    if (spec.title === 'The Efficiency Rating') return ['Feed Energy'];
    return [];
  }
  if (spec.gradeLevel === 10) {
    if (spec.title === 'The Physics of Stopping') return ['Rigid Wall', 'Sponge Wall'];
    if (spec.title === 'Rolling With The Punches') return ['Stationary Target', 'Rolling-Back Target'];
    if (spec.title === 'The Collision Math') return ['Start Collision'];
    if (spec.title === 'Zero Initial Momentum') return ['Fire'];
    if (spec.title === 'Rocket Propulsion Revisited') return ['Ignite'];
    if (spec.title === 'The Plumb Line Test') return ['Drop Line', 'Rotate and Pin Again'];
    return [];
  }
  if (spec.title === 'Pushes and Pulls') return ['Push', 'Pull'];
  if (spec.title === 'Balanced vs. Unbalanced') return ['Balanced', 'Unbalanced'];
  if (spec.title === 'Weight vs. Mass') return ['Earth', 'Moon'];
  if (spec.title === 'Smooth vs. Rough') return ['Smooth', 'Rough'];
  if (spec.title === 'Braking vs. Pedaling') return ['Pedal', 'Brake'];
  if (spec.title === 'Relative Speed') return ['Your speed = 0', "Your speed = car's speed"];
  if (spec.title === 'The Magic Beam') return ['Far out', 'Closer'];
  if (isTradingDistance) return ['Push Lever'];
  if (spec.title === 'Multiplying Strength') return ['1 Pulley', '2 Pulleys', '4 Pulleys'];
  if (isCatapultRelease) return ['Release'];
  if (spec.title === "Gravity's Battery") return ['Drop'];
  if (spec.title === 'The Three Paths of Heat') return ['Conduction', 'Convection', 'Radiation'];
  if (spec.title === 'The Floating Fluid') return ['Add Heat'];
  if (spec.title === 'The Wheel and Axle') return ['Turn Wheel'];
  if (spec.title === 'The Inclined Plane') return ['Lift', 'Use ramp'];
  if (spec.title === 'The Stubbornness of Stuff') return ['Push'];
  if (spec.title === 'The Seatbelt Savior') return ['No Seatbelt', 'Seatbelt On'];
  if (spec.title === 'The Unbalanced Force') return ['Balanced', 'Unbalanced'];
  if (spec.title === 'Mass and Inertia') return ['Standard Push'];
  if (spec.title === 'The Seatbelt Lock') return ['CRASH'];
  if (spec.title === 'The Math of the Push') return ['Apply'];
  if (spec.title === 'The Universal Constant') return ['Earth', 'Deep Space', 'Moon'];
  if (spec.title === 'Weight is a Force') return ['Earth', 'Moon'];
  if (spec.title === 'Adding Up The Pushes') return ['Calculate'];
  if (spec.title === 'The Rocket in the Vacuum') return ['Ignite'];
  if (spec.title === 'Mass in Motion') return ['Deploy Brakes'];
  if (spec.title === 'Stretching the Clock') return ['Rigid Wall', 'Deploy Airbags'];
  if (isRuleOfLaziness) return ['Add Wall'];
  return [];
};

export function PhysicsConceptSimulation({ lessonSlug, pageIndex, pageData, size = 520 }: PhysicsConceptSimulationProps) {
  const spec = useMemo(() => createSpec(lessonSlug, pageIndex, pageData), [lessonSlug, pageIndex, pageData]);
  const [time, setTime] = useState(0);
  const [selected, setSelected] = useState(0);
  const [slider, setSlider] = useState(55);
  const [sliderB, setSliderB] = useState(45);
  const [actionCount, setActionCount] = useState(0);
  const svgWidth = Math.min(size, 680);
  const svgHeight = Math.round(svgWidth * 0.58);
  const isRuleOfLaziness = spec.title === 'The Rule of Laziness';
  const isTradingDistance = spec.title === 'Trading Distance for Strength';
  const isCatapultRelease = spec.title === 'Energy in Action and in Waiting';
  const usesCustomSlider = needsCustomSlider(spec);
  const buttons = spec.mode === 'calibration'
    ? calibrationButtonsFor(spec, isTradingDistance, isCatapultRelease, isRuleOfLaziness)
    : spec.mode === 'buttons'
      ? spec.buttons
      : [];
  const primaryControlLabel = primaryControlFor(spec);
  const secondaryControlLabel = secondaryControlFor(spec);

  useEffect(() => {
    setSelected(0);
    setSlider(55);
    setSliderB(45);
    setActionCount(0);
  }, [spec.id]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTime((value) => (value + 1) % 10000);
    }, 60);
    return () => window.clearInterval(interval);
  }, []);

  const handleButton = (index: number) => {
    if (spec.gradeLevel === 9 || spec.gradeLevel === 10) {
      setSelected(buttons.length <= 1 ? 1 : index);
      setActionCount((value) => value + 1);
      setTime(0);
      return;
    }
    if (isTradingDistance || isCatapultRelease || spec.title === 'The Wheel and Axle') {
      setActionCount((value) => Math.min(7, value + 1));
      setTime(0);
      return;
    }
    if ([
      "Gravity's Battery",
      'The Floating Fluid',
      'The Stubbornness of Stuff',
      'Mass and Inertia',
      'The Seatbelt Lock',
      'The Math of the Push',
      'Adding Up The Pushes',
      'The Rocket in the Vacuum',
      'Mass in Motion',
    ].includes(spec.title)) {
      setSelected(1);
      setTime(0);
      return;
    }
    if (isRuleOfLaziness) {
      setSelected(1);
      setTime(0);
      return;
    }
    setSelected(index);
    setTime(0);
  };

  return (
    <div
      style={shellStyle}
      data-simulation-id={spec.id}
      data-description-hash={spec.hash}
      data-scene-kind={spec.sceneKind}
      data-object-keys={spec.labels.join(',')}
        data-control-count={needsSecondSlider(spec) ? 2 : (spec.mode === 'slider' || usesCustomSlider) ? 1 : 0}
      data-button-count={buttons.length}
      data-drag-enabled="false"
    >
      <svg
        viewBox="0 0 720 410"
        width={svgWidth}
        height={svgHeight}
        role="img"
        aria-label={`${spec.title} simple physics animation`}
        style={{ display: 'block' }}
      >
        <defs>
          <radialGradient id={`simple-glow-${spec.id}`} cx="50%" cy="42%" r="70%">
            <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#08111f" stopOpacity="0" />
          </radialGradient>
        </defs>
        <rect x="0" y="0" width="720" height="410" rx="18" fill="#08111f" />
        <rect x="20" y="54" width="680" height="304" rx="18" fill={`url(#simple-glow-${spec.id})`} stroke="rgba(148,163,184,0.18)" />
        <text x="34" y="31" fill="#93c5fd" fontSize="12" fontWeight="900">{spec.sceneKind.toUpperCase()}</text>
        <text x="34" y="51" fill="#e5eefc" fontSize="18" fontWeight="900">{compact(spec.title, 'Physics')}</text>
        {spec.mode === 'calibration'
          ? <CalibrationScene spec={spec} selected={selected} slider={slider} sliderB={sliderB} actionCount={actionCount} time={time} />
          : <GeneralScene spec={spec} selected={selected} slider={slider} time={time} />}
      </svg>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 10 }}>
        {buttons.map((button, index) => (
          <button key={button} type="button" style={buttonStyle(selected === index || (isRuleOfLaziness && selected === 1))} onClick={() => handleButton(index)}>
            {button}
          </button>
        ))}
        {(spec.mode === 'slider' || usesCustomSlider) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#cbd5e1', fontSize: 13, fontWeight: 800, minWidth: 240 }}>
            {primaryControlLabel}
            <input
              aria-label={`${primaryControlLabel} control`}
              type="range"
              min="0"
              max="100"
              value={slider}
              onChange={(event) => setSlider(Number(event.target.value))}
              style={{ flex: 1 }}
            />
          </label>
        )}
        {needsSecondSlider(spec) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#cbd5e1', fontSize: 13, fontWeight: 800, minWidth: 240 }}>
            {secondaryControlLabel}
            <input
              aria-label={`${secondaryControlLabel} control`}
              type="range"
              min="0"
              max="100"
              value={sliderB}
              onChange={(event) => setSliderB(Number(event.target.value))}
              style={{ flex: 1 }}
            />
          </label>
        )}
        {!buttons.length && spec.mode !== 'slider' && !usesCustomSlider && (
          <span style={{ color: '#cbd5e1', fontSize: 13, fontWeight: 800, border: '1px solid rgba(148,163,184,0.24)', borderRadius: 999, padding: '6px 10px' }}>
            looping animation
          </span>
        )}
      </div>
    </div>
  );
}
